import React, { useEffect, useRef, useState, useCallback } from 'react'
import { X } from 'lucide-react'
import { apiClient } from '../config/api'
import { Corridor, Geofence } from './CorridorManager'
import { 
  GeofencePayload, 
  validateGeofencePayload, 
  normalizePolygon,
  GeofenceType 
} from '../types/geofence'
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader'
import { useAuth } from '../contexts/AuthContext'
import { resolvePlaceName } from '../utils/placeNames'

/** Representative point of a drawn shape: centre, bounds centre or centroid. */
function overlayCenter(
  overlay: google.maps.Circle | google.maps.Rectangle | google.maps.Polygon | google.maps.Marker | null
): google.maps.LatLng | null {
  if (!overlay) return null
  if (overlay instanceof google.maps.Circle) return overlay.getCenter() || null
  if (overlay instanceof google.maps.Rectangle) return overlay.getBounds()?.getCenter() || null
  if (overlay instanceof google.maps.Marker) return overlay.getPosition() || null
  if (overlay instanceof google.maps.Polygon) {
    const path = overlay.getPath()
    if (!path || path.getLength() === 0) return null
    let lat = 0
    let lng = 0
    for (let i = 0; i < path.getLength(); i++) {
      lat += path.getAt(i).lat()
      lng += path.getAt(i).lng()
    }
    return new google.maps.LatLng(lat / path.getLength(), lng / path.getLength())
  }
  return null
}

interface CorridorMapProps {
  corridors: Corridor[]
  geofences: Geofence[]
  selectedCorridor?: Corridor | null
  selectedGeofence?: Geofence | null
  // Support for multiple selections (for alert creation)
  selectedGeofenceIds?: string[]
  selectedCorridorIds?: string[]
  // Interactive selection callbacks for alerts
  onGeofenceToggle?: (geofenceId: string) => void
  onCorridorToggle?: (corridorId: string) => void
  // Preview mode - show all items for selection
  isPreviewMode?: boolean
  isDrawingMode?: boolean
  drawingType?: 'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point'
  onDrawingComplete?: (points: Array<{ lat: number, lng: number }>) => void
  isEditingMode?: boolean
  editingCorridor?: Corridor | null
  onCorridorUpdate?: (corridorId: string, points: Array<{ lat: number, lng: number, address?: string }>) => void
  centerOnCorridor?: boolean
  centerOnGeofence?: boolean
  isEditingGeofence?: boolean
  editingGeofence?: Geofence | null
  onGeofenceUpdate?: (geofenceId: string, geofenceData: any) => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
  initialDrawingPoint?: { lat: number, lng: number }
  initialDrawingZoom?: number
  initialReferenceTrajectory?: Array<{ lat: number, lng: number }>
  initialVehicle?: {
    id: string
    label: string
    dateRange: { startDate: string, endDate: string }
    source?: 'ASSET' | 'CONTAINER'
    assetType?: string
    trackingStatus?: 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'
    heading?: number
  }
  initialDrawingRequestId?: number
  initialDrawingLabel?: string
}

const CorridorMap: React.FC<CorridorMapProps> = ({
  corridors,
  geofences,
  selectedCorridor,
  selectedGeofence,
  selectedGeofenceIds = [],
  selectedCorridorIds = [],
  onGeofenceToggle,
  onCorridorToggle,
  isPreviewMode = false,
  isDrawingMode = false,
  drawingType = 'trajectory',
  onDrawingComplete,
  isEditingMode = false,
  editingCorridor = null,
  onCorridorUpdate,
  centerOnCorridor = false,
  centerOnGeofence = false,
  isEditingGeofence = false,
  editingGeofence = null,
  onGeofenceUpdate,
  showMessage,
  showConfirm,
  initialDrawingPoint,
  initialDrawingZoom,
  initialReferenceTrajectory,
  initialVehicle,
  initialDrawingRequestId,
  initialDrawingLabel
}) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null)
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null)
  const corridorOverlaysRef = useRef<Map<string, google.maps.Polyline>>(new Map())
  const geofenceOverlaysRef = useRef<Map<string, google.maps.Polygon | google.maps.Circle | google.maps.Rectangle | google.maps.Marker>>(new Map())
  const drawingMarkersRef = useRef<google.maps.Marker[]>([])
  const drawingPathRef = useRef<google.maps.Polyline | null>(null)
  const referenceTrajectoryRef = useRef<google.maps.Polyline | null>(null)
  const referenceVehicleMarkerRef = useRef<google.maps.Marker | null>(null)
  const referenceRequestIdRef = useRef<number | undefined>(undefined)
  const mapListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const waypointMarkersRef = useRef<google.maps.Marker[]>([])
  
  // Use secure Google Maps loader
  const { isLoaded: isMapsLoaded, isLoading: isMapsLoading, error: mapsError } = useGoogleMapsLoader({
    libraries: ['geometry', 'places'],
    retryAttempts: 3,
    retryDelay: 1000
  })
  
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [drawingPoints, setDrawingPoints] = useState<Array<{ lat: number, lng: number, address?: string }>>([])
  const [showWaypointPanel, setShowWaypointPanel] = useState(false)
  const [editingPoints, setEditingPoints] = useState<Array<{ lat: number, lng: number, address?: string }>>([])
  const [editingMarkersRef] = useState(useRef<google.maps.Marker[]>([]))
  const [editingGeofenceOverlay, setEditingGeofenceOverlay] = useState<google.maps.Circle | google.maps.Polygon | google.maps.Rectangle | google.maps.Marker | null>(null)
  const [hasCreatedGeofence, setHasCreatedGeofence] = useState(false)
  // Vertices of a polygon still being clicked out. Save stays hidden until
  // this reaches 3, because the old "double-click to finish" never fired:
  // the map consumes the double-click to zoom.
  const [draftPolygonCount, setDraftPolygonCount] = useState(0)
  const finalizePolygonRef = useRef<(() => boolean) | null>(null)
  // Where the freshly drawn zone is, in words, so the operator names it after
  // the place instead of reading coordinates off the map.
  const [createdGeofencePlace, setCreatedGeofencePlace] = useState<{ name: string | null; coords: string } | null>(null)
  const currentGeofenceRef = useRef<google.maps.Circle | google.maps.Polygon | google.maps.Rectangle | google.maps.Marker | null>(null)
  const [routeInfo, setRouteInfo] = useState<{
    name: string
    color: string
    distance: number
    duration: number
    countries: string[]
  }>({
    name: '',
    color: '#10B981',
    distance: 0,
    duration: 0,
    countries: []
  })
  const [selectedGeofenceType, setSelectedGeofenceType] = useState<'circle' | 'square' | 'triangle' | 'polygon' | 'point'>('circle')

  // Name the place of a freshly drawn zone. Reads the shape from the ref the
  // drawing handlers fill, so every shape type is covered by one effect.
  useEffect(() => {
    if (!hasCreatedGeofence) {
      setCreatedGeofencePlace(null)
      return
    }
    const center = overlayCenter(currentGeofenceRef.current)
    if (!center) return
    const coords = `${center.lat().toFixed(5)}, ${center.lng().toFixed(5)}`
    let cancelled = false
    setCreatedGeofencePlace({ name: null, coords })
    resolvePlaceName(center.lat(), center.lng()).then(name => {
      if (!cancelled) setCreatedGeofencePlace({ name, coords })
    })
    return () => { cancelled = true }
  }, [hasCreatedGeofence])

  // Customer ownership: forced for tenant accounts, optional picker for global users
  const { customerId: authCustomerId } = useAuth()
  const [drawCustomerId, setDrawCustomerId] = useState<string>('')
  const [customerOptions, setCustomerOptions] = useState<Array<{ id: number; company_name: string; customer_code: string }>>([])
  useEffect(() => {
    if (!isDrawingMode || authCustomerId != null) return
    apiClient.get('/customers')
      .then(res => setCustomerOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCustomerOptions([]))
  }, [isDrawingMode, authCustomerId])
  const [selectedPlaceType, setSelectedPlaceType] = useState<'gas_station' | 'parking' | 'office' | 'warehouse' | 'border' | 'port' | 'airport' | 'bridge' | 'police' | 'control_point' | 'security_zone' | 'custom'>('custom')
  const [selectedPurpose, setSelectedPurpose] = useState<'avoid' | 'mandatory' | 'checkpoint' | 'authorized' | 'restricted' | 'forbidden' | 'tracking'>('tracking')
  const [geofenceParameters, setGeofenceParameters] = useState({
    // Alert Settings (Universal)
    alertOnEntry: false,
    alertOnExit: false,
    alertMessage: '',
    
    // Speed Control
    speedLimit: undefined,
    speedAlert: false,
    speedTolerance: 10,
    
    // Authorization
    authorizationLevel: 'public' as 'public' | 'restricted' | 'private' | 'emergency',
    allowedVehicleTypes: [],
    requiredPermits: [],
    
    // Time Restrictions
    timeRestrictions: {
      allowedHours: [],
      allowedDays: []
    },
    
    // Monitoring
    dwellTimeLimit: 0,
    customProperties: {}
  })

  // Note: selectedGeofenceType is controlled by user selection in the panel
  // Don't auto-update it based on drawingType to preserve user choice

  // Initialize map with secure loader
  const initializeMap = useCallback(async () => {
    if (!mapRef.current || !isMapsLoaded) return

    try {
      setIsLoading(true)
      setError(null)

      createMap()
    } catch (err: any) {
      console.error('❌ Corridor map initialization error:', err)
      setError(err.message || 'Failed to initialize map')
    } finally {
      setIsLoading(false)
    }
  }, [isMapsLoaded])

  // Update loading and error states based on Maps loader
  useEffect(() => {
    if (mapsError) {
      setError(mapsError)
      setIsLoading(false)
    } else if (isMapsLoaded) {
      initializeMap()
    } else if (isMapsLoading) {
      setIsLoading(true)
      setError(null)
    }
  }, [isMapsLoaded, isMapsLoading, mapsError, initializeMap])

  const createMap = () => {
    if (!mapRef.current || !window.google?.maps) return

    const mapOptions: google.maps.MapOptions = {
      center: { lat: 12.6392, lng: -8.0029 }, // Bamako, Mali
      zoom: 8, // Bigger/wider view when map opens
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      // Enable Google Maps UI controls
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
        mapTypeIds: [
          google.maps.MapTypeId.ROADMAP,
          google.maps.MapTypeId.SATELLITE,
          google.maps.MapTypeId.HYBRID,
          google.maps.MapTypeId.TERRAIN
        ]
      },
      scaleControl: true,
      streetViewControl: false,
      rotateControl: true,
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT
      },
      gestureHandling: 'greedy'
    }

    mapInstanceRef.current = new google.maps.Map(mapRef.current, mapOptions)
    console.log('✅ Corridor map instance created')

    // Initialize Directions Service and Renderer for road snapping
    directionsServiceRef.current = new google.maps.DirectionsService()
    directionsRendererRef.current = new google.maps.DirectionsRenderer({
      suppressMarkers: true, // We'll use our own markers
      polylineOptions: {
        strokeColor: '#10B981',
        strokeOpacity: 0.8,
        strokeWeight: 5,
        zIndex: 2500
      }
    })
    directionsRendererRef.current.setMap(mapInstanceRef.current)

    // Display existing corridors and geofences
    displayCorridors()
    displayGeofences()

    // Set up drawing mode if enabled
    if (isDrawingMode) {
      enableDrawingMode()
    }
  }

  // Display corridors on map with road-following paths
  const displayCorridors = () => {
    if (!mapInstanceRef.current || !directionsServiceRef.current) {
      console.warn('⚠️ displayCorridors called but map instance not ready')
      return
    }

    console.log('🗺️ displayCorridors called:', {
      totalCorridors: corridors.length,
      selectedCorridorId: selectedCorridor?.id,
      selectedCorridorName: selectedCorridor?.name,
      editingCorridorId: editingCorridor?.id
    })

    // Clear existing corridor overlays
    corridorOverlaysRef.current.forEach(overlay => overlay.setMap(null))
    corridorOverlaysRef.current.clear()

    let displayedCount = 0
    corridors.forEach(async (corridor) => {
      // Safety checks for corridor data
      if (!corridor || !corridor.points || !Array.isArray(corridor.points) || corridor.points.length < 2) {
        console.log(`⏭️ Skipping invalid corridor:`, corridor)
        return
      }
      
      if (corridor.isActive !== false) { // Default to active if not specified
        
        // Display logic: show selected items OR show all items if in selection/preview mode
        const isSelectedForEditing = selectedCorridor?.id === corridor.id || editingCorridor?.id === corridor.id
        const isSelectedForAlert = selectedCorridorIds.includes(corridor.id)
        const isInSelectionMode = onCorridorToggle !== undefined
        
        // Show corridor if: selected for editing, selected for alert, in selection mode, or in preview mode
        if (!isSelectedForEditing && !isSelectedForAlert && !isInSelectionMode && !isPreviewMode) {
          console.log(`⏭️ Skipping non-selected corridor: ${corridor.name}`)
          return // Skip non-selected corridors
        }
        
        displayedCount++
        console.log(`✅ Displaying corridor: ${corridor.name}`, {
          id: corridor.id,
          name: corridor.name,
          points: corridor.points?.length,
          color: corridor.color,
          width: corridor.width,
          isSelectedForEditing,
          isSelectedForAlert,
          isInSelectionMode,
          isPreviewMode
        })
        try {
          // Create road-following path using Directions API
          const origin = corridor.points[0]
          const destination = corridor.points[corridor.points.length - 1]
          const middlePoints = corridor.points.slice(1, -1)
          
          // Limit waypoints to 23 (25 - origin - destination = 23)
          const limitedWaypoints = middlePoints.length > 23 
            ? middlePoints.filter((_, index) => index % Math.ceil(middlePoints.length / 23) === 0).slice(0, 23)
            : middlePoints
          
          const waypoints = limitedWaypoints.map(point => ({
            location: new google.maps.LatLng(point.lat, point.lng),
            stopover: true
          }))
          
          if (middlePoints.length > 23) {
            console.log(`⚠️ Reduced waypoints from ${middlePoints.length} to ${limitedWaypoints.length} due to Google Maps limit`)
          }

          const request: google.maps.DirectionsRequest = {
            origin: new google.maps.LatLng(origin.lat, origin.lng),
            destination: new google.maps.LatLng(destination.lat, destination.lng),
            waypoints: waypoints,
            travelMode: google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
            avoidHighways: false,
            avoidTolls: false
          }

          directionsServiceRef.current!.route(request, (result, status) => {
            if (status === google.maps.DirectionsStatus.OK && result) {
              // Create a custom polyline from the directions result
              const path: google.maps.LatLng[] = []
              
              result.routes[0].legs.forEach(leg => {
                leg.steps.forEach(step => {
                  step.path.forEach(point => {
                    path.push(point)
                  })
                })
              })

        const isSelectedForEditing = selectedCorridor?.id === corridor.id
        const isSelectedForAlert = selectedCorridorIds.includes(corridor.id)
        
        // Different styles for editing vs alert selection
        let strokeColor = corridor.color || '#3B82F6'
        let strokeOpacity = 0.7
        let strokeWeight = corridor.width || 3
        let zIndex = 500
        
        if (isSelectedForEditing) {
          // Editing selection: thick blue line
          strokeColor = '#3B82F6'
          strokeOpacity = 1.0
          strokeWeight = (corridor.width || 3) + 6
          zIndex = 1000
        } else if (isSelectedForAlert) {
          // Alert selection: thick green line
          strokeColor = '#10B981'
          strokeOpacity = 1.0
          strokeWeight = (corridor.width || 3) + 4
          zIndex = 800
        }
        
        const polyline = new google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: strokeColor || '#3B82F6',
          strokeOpacity,
          strokeWeight: strokeWeight || 3,
          zIndex
        })

        polyline.setMap(mapInstanceRef.current)
        corridorOverlaysRef.current.set(corridor.id, polyline)
        console.log(`🗺️ Corridor polyline created and added to map: ${corridor.name}`, {
          pathLength: path.length,
          strokeColor,
          strokeWeight,
          zIndex
        })

        // Add click listener
        polyline.addListener('click', () => {
          console.log(`Clicked corridor: ${corridor.name}`)
          
          // If in selection mode, toggle selection
          if (onCorridorToggle) {
            const isCurrentlySelected = selectedCorridorIds.includes(corridor.id)
            const action = isCurrentlySelected ? 'remove' : 'add'
            const message = isCurrentlySelected 
              ? `Remove "${corridor.name}" from alert?`
              : `Add "${corridor.name}" to alert?`
            
            if (confirm(message)) {
              console.log(`${action === 'add' ? '✅ Adding' : '❌ Removing'} corridor: ${corridor.name}`)
              onCorridorToggle(corridor.id)
            }
          }
        })
              
              console.log(`✅ Road-following path created for corridor: ${corridor.name}`)
            } else {
              console.warn(`⚠️ Directions failed for ${corridor.name}, using straight line`)
              // Fallback to straight line if directions fail
              const isSelectedForEditing = selectedCorridor?.id === corridor.id
              const isSelectedForAlert = selectedCorridorIds.includes(corridor.id)
              
              // Different styles for editing vs alert selection
              let strokeColor = corridor.color || '#3B82F6'
              let strokeOpacity = 0.7
              let strokeWeight = corridor.width || 3
              let zIndex = 500
              
              if (isSelectedForEditing) {
                // Editing selection: thick blue line
                strokeColor = '#3B82F6'
                strokeOpacity = 1.0
                strokeWeight = (corridor.width || 3) + 6
                zIndex = 1000
              } else if (isSelectedForAlert) {
                // Alert selection: thick green line
                strokeColor = '#10B981'
                strokeOpacity = 1.0
                strokeWeight = (corridor.width || 3) + 4
                zIndex = 800
              }
              
              const polyline = new google.maps.Polyline({
                path: corridor.points,
                geodesic: true,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                zIndex,
                strokePattern: [10, 5] // Dashed line to indicate fallback
              })

              polyline.setMap(mapInstanceRef.current)
              corridorOverlaysRef.current.set(corridor.id, polyline)
              console.log(`🗺️ Fallback corridor polyline created: ${corridor.name}`, {
                pointsCount: corridor.points.length,
                strokeColor,
                strokeWeight,
                zIndex
              })

        polyline.addListener('click', () => {
          console.log(`Clicked corridor: ${corridor.name}`)
        })
            }
          })
          
        } catch (error) {
          console.error(`❌ Error creating road path for ${corridor.name}:`, error)
          // Fallback to straight line
          const isSelected = selectedCorridor?.id === corridor.id
          const polyline = new google.maps.Polyline({
            path: corridor.points,
            geodesic: true,
            strokeColor: corridor.color, // Keep original color
            strokeOpacity: isSelected ? 1.0 : 0.7,
            strokeWeight: isSelected ? corridor.width + 6 : corridor.width, // Much thicker when selected
            zIndex: isSelected ? 1000 : 500,
            strokePattern: [10, 5] // Dashed line to indicate fallback
          })

          polyline.setMap(mapInstanceRef.current)
          corridorOverlaysRef.current.set(corridor.id, polyline)

          polyline.addListener('click', () => {
            console.log(`Clicked corridor: ${corridor.name}`)
            
            // If in selection mode, toggle selection
            if (onCorridorToggle) {
              const isCurrentlySelected = selectedCorridorIds.includes(corridor.id)
              const action = isCurrentlySelected ? 'remove' : 'add'
              const message = isCurrentlySelected 
                ? `Remove "${corridor.name}" from alert?`
                : `Add "${corridor.name}" to alert?`
              
              if (confirm(message)) {
                console.log(`${action === 'add' ? '✅ Adding' : '❌ Removing'} corridor: ${corridor.name}`)
                onCorridorToggle(corridor.id)
              }
            }
          })
        }
      }
    })
  }

  // Display geofences on map
  const displayGeofences = () => {
    if (!mapInstanceRef.current) {
      console.warn('⚠️ displayGeofences called but map instance not ready')
      return
    }

    console.log('🗺️ displayGeofences called:', {
      totalGeofences: geofences.length,
      selectedGeofenceId: selectedGeofence?.id,
      selectedGeofenceName: selectedGeofence?.name,
      editingGeofenceId: editingGeofence?.id
    })

    // Clear existing geofence overlays
    geofenceOverlaysRef.current.forEach(overlay => overlay.setMap(null))
    geofenceOverlaysRef.current.clear()

    let displayedCount = 0
    geofences.forEach(geofence => {
      // Safety checks for geofence data
      if (!geofence) {
        console.log(`⏭️ Skipping invalid geofence:`, geofence)
        return
      }
      
      if (geofence.isActive === false) {
        console.log(`⏭️ Skipping inactive geofence: ${geofence.name}`)
        return
      }
      
      // Display logic: show selected items OR show all items if in selection/preview mode
      const isSelectedForEditing = selectedGeofence?.id === geofence.id || editingGeofence?.id === geofence.id
      const isSelectedForAlert = selectedGeofenceIds.includes(geofence.id)
      const isInSelectionMode = onGeofenceToggle !== undefined
      
      // Show geofence if: selected for editing, selected for alert, in selection mode, or in preview mode
      if (!isSelectedForEditing && !isSelectedForAlert && !isInSelectionMode && !isPreviewMode) {
        console.log(`⏭️ Skipping non-selected geofence: ${geofence.name}`)
        return // Skip non-selected geofences
      }
      
      displayedCount++
      console.log(`✅ Displaying geofence: ${geofence.name} (${geofence.type})`, {
        id: geofence.id,
        type: geofence.type,
        center: geofence.center,
        radius: geofence.radius,
        bounds: geofence.bounds?.length,
        color: geofence.color,
        fillColor: geofence.fillColor,
        isSelectedForEditing,
        isSelectedForAlert,
        isInSelectionMode
      })

      let overlay: google.maps.Circle | google.maps.Polygon | null = null

      if (geofence.type === 'circle' && geofence.center && geofence.radius) {
        console.log(`🔵 Creating circle for: ${geofence.name}`)
        const isSelectedForEditing = selectedGeofence?.id === geofence.id
        const isSelectedForAlert = selectedGeofenceIds.includes(geofence.id)
        
        // Different styles for editing vs alert selection
        let strokeColor = geofence.color || '#3B82F6'
        let strokeOpacity = 0.8
        let strokeWeight = 2
        let fillOpacity = 0.3
        let zIndex = 500
        
        if (isSelectedForEditing) {
          // Editing selection: thick blue border
          strokeColor = '#3B82F6'
          strokeOpacity = 1.0
          strokeWeight = 6
          fillOpacity = 0.6
          zIndex = 1000
        } else if (isSelectedForAlert) {
          // Alert selection: thick green border
          strokeColor = '#10B981'
          strokeOpacity = 1.0
          strokeWeight = 4
          fillOpacity = 0.5
          zIndex = 800
        }
        
        overlay = new google.maps.Circle({
          center: geofence.center,
          radius: geofence.radius,
          strokeColor,
          strokeOpacity,
          strokeWeight,
          fillColor: geofence.fillColor || '#3B82F6',
          fillOpacity,
          zIndex
        })
      } else if (geofence.bounds && geofence.bounds.length >= 3) {
        console.log(`🔷 Creating polygon for: ${geofence.name}`, {
          boundsLength: geofence.bounds.length,
          bounds: geofence.bounds
        })
        const isSelectedForEditing = selectedGeofence?.id === geofence.id
        const isSelectedForAlert = selectedGeofenceIds.includes(geofence.id)
        
        // Different styles for editing vs alert selection
        let strokeColor = geofence.color || '#3B82F6'
        let strokeOpacity = 0.8
        let strokeWeight = 2
        let fillOpacity = 0.3
        let zIndex = 500
        
        if (isSelectedForEditing) {
          // Editing selection: thick blue border
          strokeColor = '#3B82F6'
          strokeOpacity = 1.0
          strokeWeight = 6
          fillOpacity = 0.6
          zIndex = 1000
        } else if (isSelectedForAlert) {
          // Alert selection: thick green border
          strokeColor = '#10B981'
          strokeOpacity = 1.0
          strokeWeight = 4
          fillOpacity = 0.5
          zIndex = 800
        }
        
        overlay = new google.maps.Polygon({
          paths: geofence.bounds,
          strokeColor,
          strokeOpacity,
          strokeWeight,
          fillColor: geofence.fillColor || '#3B82F6',
          fillOpacity,
          zIndex
        })
      } else if (geofence.type === 'point' && geofence.center) {
        console.log(`📍 Creating point marker for: ${geofence.name}`)
        console.log('📍 Creating point geofence display:', geofence.name)
        const isSelectedForEditing = selectedGeofence?.id === geofence.id
        const isSelectedForAlert = selectedGeofenceIds.includes(geofence.id)
        
        // Different styles for editing vs alert selection
        let scale = 12
        let fillOpacity = 0.8
        let strokeWeight = 2
        let zIndex = 500
        let strokeColor = '#ffffff'
        
        if (isSelectedForEditing) {
          // Editing selection: larger blue marker
          scale = 16
          fillOpacity = 1.0
          strokeWeight = 3
          strokeColor = '#3B82F6'
          zIndex = 1000
        } else if (isSelectedForAlert) {
          // Alert selection: larger green marker
          scale = 14
          fillOpacity = 1.0
          strokeWeight = 3
          strokeColor = '#10B981'
          zIndex = 800
        }
        
        // Create point marker for display
        const pointMarker = new google.maps.Marker({
          position: geofence.center,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale,
            fillColor: geofence.color || '#3B82F6',
            fillOpacity,
            strokeColor,
            strokeWeight,
            strokeOpacity: 1
          },
          title: geofence.name,
          zIndex
        })
        
        overlay = pointMarker as any // Cast for type compatibility
      }

      if (overlay) {
        overlay.setMap(mapInstanceRef.current)
        geofenceOverlaysRef.current.set(geofence.id, overlay)
        console.log(`✅ Geofence overlay added to map: ${geofence.name} (${geofence.type})`, {
          overlayType: overlay.constructor.name,
          center: geofence.center,
          radius: geofence.radius,
          bounds: geofence.bounds?.length
        })

        // Add click listener
        overlay.addListener('click', () => {
          console.log(`Clicked geofence: ${geofence.name}`)
          
          // If in selection mode, toggle selection
          if (onGeofenceToggle) {
            const isCurrentlySelected = selectedGeofenceIds.includes(geofence.id)
            const action = isCurrentlySelected ? 'remove' : 'add'
            const message = isCurrentlySelected 
              ? `Remove "${geofence.name}" from alert?`
              : `Add "${geofence.name}" to alert?`
            
            if (confirm(message)) {
              console.log(`${action === 'add' ? '✅ Adding' : '❌ Removing'} geofence: ${geofence.name}`)
              onGeofenceToggle(geofence.id)
            }
          }
        })
      } else {
        console.warn(`⚠️ Failed to create overlay for geofence: ${geofence.name}`, {
          type: geofence.type,
          hasCenter: !!geofence.center,
          hasRadius: !!geofence.radius,
          hasBounds: !!geofence.bounds,
          boundsLength: geofence.bounds?.length
        })
        console.warn(`Creating fallback marker...`)
        
        // Create fallback marker if no overlay could be created
        if (geofence.center) {
          const fallbackMarker = new google.maps.Marker({
            position: geofence.center,
            map: mapInstanceRef.current,
            title: `${geofence.name} (Fallback Display)`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: geofence.color || '#3B82F6',
              fillOpacity: 0.8,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              strokeOpacity: 1
            }
          })
          
          // Add click listener to fallback marker
          fallbackMarker.addListener('click', () => {
            console.log(`Clicked fallback geofence marker: ${geofence.name}`)
            
            if (onGeofenceToggle) {
              const isCurrentlySelected = selectedGeofenceIds.includes(geofence.id)
              const action = isCurrentlySelected ? 'remove' : 'add'
              const message = isCurrentlySelected 
                ? `Remove "${geofence.name}" from alert?`
                : `Add "${geofence.name}" to alert?`
              
              if (confirm(message)) {
                console.log(`${action === 'add' ? '✅ Adding' : '❌ Removing'} geofence: ${geofence.name}`)
                onGeofenceToggle(geofence.id)
              }
            }
          })
          
          geofenceOverlaysRef.current.set(geofence.id, fallbackMarker as any)
          console.log(`✅ Fallback marker created for geofence: ${geofence.name}`)
        } else {
          console.error(`❌ Cannot display geofence ${geofence.name}: no center or bounds data`)
        }
      }
    })
    
    console.log(`📊 displayGeofences complete: ${displayedCount} geofences displayed`)
  }

  const showVehicleCreationContext = () => {
    if (!mapInstanceRef.current || !initialDrawingPoint) return

    const requestId = initialDrawingRequestId
    referenceRequestIdRef.current = requestId
    referenceTrajectoryRef.current?.setMap(null)
    referenceVehicleMarkerRef.current?.setMap(null)

    const drawReferenceTrajectory = (points: Array<{ lat: number, lng: number }>) => {
      if (!mapInstanceRef.current || referenceRequestIdRef.current !== requestId || points.length < 2) return
      referenceTrajectoryRef.current?.setMap(null)
      referenceTrajectoryRef.current = new google.maps.Polyline({
        path: points,
        map: mapInstanceRef.current,
        geodesic: true,
        strokeColor: '#475569',
        strokeOpacity: 0.65,
        strokeWeight: 4,
        zIndex: 300,
        icons: [{
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 2.2,
            fillColor: '#334155',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1
          },
          offset: '6%',
          repeat: '12%'
        }]
      })
    }

    if (initialReferenceTrajectory && initialReferenceTrajectory.length > 1) {
      drawReferenceTrajectory(initialReferenceTrajectory)
    } else if (initialVehicle) {
      const start = new Date(initialVehicle.dateRange.startDate).getTime()
      const end = new Date(initialVehicle.dateRange.endDate).getTime()
      const hours = Math.max(1, Math.ceil((end - start) / 3600000) + 24)
      apiClient.get(`/timeline/${initialVehicle.id}`, {
        startDate: initialVehicle.dateRange.startDate,
        endDate: initialVehicle.dateRange.endDate,
        hours,
        vehicleType: String(initialVehicle.source || '').toLowerCase()
      }).then(response => {
        const valid = (Array.isArray(response.data) ? response.data : [])
          .filter((point: any) =>
            point.location?.latitude != null &&
            point.location?.longitude != null &&
            Number.isFinite(Number(point.location.latitude)) &&
            Number.isFinite(Number(point.location.longitude))
          )
          .map((point: any) => ({
            lat: Number(point.location.latitude),
            lng: Number(point.location.longitude)
          }))
        const stride = Math.max(1, Math.ceil(valid.length / 1000))
        const sampled = valid.filter((_: any, index: number) => index % stride === 0 || index === valid.length - 1)
        drawReferenceTrajectory(sampled)
      }).catch(error => {
        console.warn('Unable to load the vehicle reference trajectory:', error)
      })
    }

    if (initialVehicle) {
      const safeLabel = initialVehicle.label.replace(/[&<>"']/g, character => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
      }[character] || character))
      const color =
        initialVehicle.trackingStatus === 'LIVE' ? '#16A34A' :
          initialVehicle.trackingStatus === 'STALE' ? '#F59E0B' :
            initialVehicle.trackingStatus === 'OFFLINE' ? '#DC2626' : '#64748B'
      const heading = Number.isFinite(initialVehicle.heading) ? Number(initialVehicle.heading) : 0
      const glyph = initialVehicle.source === 'CONTAINER'
        ? '<rect x="13" y="19" width="38" height="26" rx="3"/><path d="M20 19v26M28 19v26M36 19v26M44 19v26"/>'
        : '<path d="M10 20h28v21H10zM38 27h9l7 8v6H38z"/><circle cx="20" cy="44" r="5"/><circle cx="46" cy="44" r="5"/>'
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><circle cx="32" cy="32" r="29" fill="${color}" stroke="#fff" stroke-width="4"/><g transform="rotate(${heading} 32 32)" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">${glyph}</g></svg>`
      const marker = new google.maps.Marker({
        position: initialDrawingPoint,
        map: mapInstanceRef.current,
        title: initialVehicle.label,
        zIndex: 3200,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new google.maps.Size(36, 36),
          anchor: new google.maps.Point(18, 18)
        }
      })
      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="padding:6px 8px"><strong>${safeLabel}</strong><br/><span style="font-size:12px;color:#64748b">${initialDrawingPoint.lat.toFixed(6)}, ${initialDrawingPoint.lng.toFixed(6)}</span></div>`
      })
      marker.addListener('click', () => infoWindow.open(mapInstanceRef.current, marker))
      referenceVehicleMarkerRef.current = marker
    }
  }

  // Enable drawing mode
  const enableDrawingMode = () => {
    if (!mapInstanceRef.current) return

    console.log(`🎨 Enabling drawing mode: ${drawingType}`)
    
    // Clear any existing drawing and listeners
    clearDrawing()
    
    // Clear all existing map listeners to prevent duplicates
    mapListenersRef.current.forEach(listener => {
      google.maps.event.removeListener(listener)
    })
    mapListenersRef.current = []
    
    if (drawingType === 'trajectory') {
      // Trajectory drawing (roads/corridors)
      enableTrajectoryDrawing()
    } else {
      // Geofence drawing (shapes) - directly enable specific shape
      console.log(`📍 Enabling ${selectedGeofenceType} geofence drawing directly`)
      
      // Show waypoint panel for geofence creation
    setShowWaypointPanel(true)
      setRouteInfo(prev => ({
        ...prev,  // Preserve existing name and other values
        color: prev.color || '#EF4444',
        distance: 0,
        duration: 0,
        countries: []
      }))
      
      // Change cursor to crosshair
    mapInstanceRef.current.setOptions({ 
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair'
      })

      // Directly enable the specific geofence type
      const shapeType = selectedGeofenceType
      console.log(`🎯 Creating geofence type: ${shapeType}`)
      console.log(`🔍 selectedGeofenceType state value: ${selectedGeofenceType}`)
      console.log(`🔍 drawingType state value: ${drawingType}`)
      
      if (shapeType === 'circle') {
        console.log('⭕ Enabling circle drawing')
        enableCircleDrawing()
      } else if (shapeType === 'square') {
        console.log('⬜ Enabling DEDICATED square drawing')
        enableSquareDrawing()
      } else if (shapeType === 'triangle') {
        console.log('🔺 Enabling triangle drawing')
        enableTriangleDrawing()
      } else if (shapeType === 'polygon') {
        console.log('📐 Enabling polygon drawing')
        enablePolygonDrawing()
      } else if (shapeType === 'point') {
        console.log('📍 Enabling point drawing')
        enablePointDrawing()
      }
    }

    if (initialDrawingPoint) {
      mapInstanceRef.current.setCenter(initialDrawingPoint)
      mapInstanceRef.current.setZoom(Math.max(3, Math.min(21, initialDrawingZoom || 15)))
      showVehicleCreationContext()
      if (drawingType === 'trajectory') {
        addDrawingPoint({
          ...initialDrawingPoint,
          address: initialDrawingLabel
            ? `${initialDrawingLabel} — current position`
            : `${initialDrawingPoint.lat.toFixed(6)}, ${initialDrawingPoint.lng.toFixed(6)}`
        })
      } else {
        // Reuse the normal shape handlers so all geofence validation and
        // editable-overlay behavior stays identical to a manual map click.
        window.setTimeout(() => {
          if (!mapInstanceRef.current) return
          google.maps.event.trigger(mapInstanceRef.current, 'click', {
            latLng: new google.maps.LatLng(initialDrawingPoint.lat, initialDrawingPoint.lng)
          })
        }, 0)
      }
    }
  }

  // Enable trajectory (road) drawing
  const enableTrajectoryDrawing = () => {
    console.log('🛣️ Enabling trajectory drawing mode')
    
    // Show waypoint panel
    setShowWaypointPanel(true)
    
    // Change cursor to crosshair for trajectory
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ 
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair'
      })
    
      // Add click listener for drawing
      const clickListener = mapInstanceRef.current.addListener('click', async (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // The place name a dispatcher would use ("Rue 581, Baco Djikoroni"),
        // from the same cached resolver as the stop popups; the full postal
        // address it replaced was mostly a plus code around Bamako.
        const address = await resolvePlaceName(point.lat, point.lng)
        addDrawingPoint({ ...point, address: address || `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}` })
      }
    })

      mapListenersRef.current.push(clickListener)
    }
  }


  // Enable circle drawing
  const enableCircleDrawing = () => {
    console.log('⭕⭕⭕ ENABLECIRCLEDRAWING FUNCTION CALLED - THIS SHOULD ONLY BE FOR CIRCLES ⭕⭕⭕')
    console.log('⭕ Enabling circle drawing')
    console.log('🔍 Current state - hasCreatedGeofence:', hasCreatedGeofence, 'editingGeofenceOverlay:', !!editingGeofenceOverlay)
    
    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        console.log('🖱️ Map clicked for circle creation. Checking for existing geofence...')
        
        // Use ref for immediate synchronous check
        if (currentGeofenceRef.current) {
          // Circle already exists - delete it on click
          console.log('🗑️ Deleting existing circle on map click')
          currentGeofenceRef.current.setMap(null)
          currentGeofenceRef.current = null
          setEditingGeofenceOverlay(null)
          setHasCreatedGeofence(false)
          console.log('✅ Circle deleted. Click again to create new circle.')
          return
        }
        
        // First click - create circle
        const center = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on clicked location
        mapInstanceRef.current!.setCenter(center)
        mapInstanceRef.current!.setZoom(15)  // Moderate zoom
        
        // Create a default circle at clicked location
        const circle = new google.maps.Circle({
          center: center,
          radius: 500, // Default 500m radius
          strokeColor: routeInfo.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: routeInfo.color + '40',
          fillOpacity: 0.3,
          editable: true,
          draggable: true,
          zIndex: 1000
        })

        circle.setMap(mapInstanceRef.current)
        currentGeofenceRef.current = circle  // Store in ref for immediate access
        setEditingGeofenceOverlay(circle)  // Store for later saving
        setHasCreatedGeofence(true)  // Mark that geofence has been created
        
        console.log('⭕ Circle created at clicked location:', center)
        console.log('🔍 Map zoomed to moderate level for editing')
        console.log('💡 Circle ready for editing. Click again to delete, or enter name and save.')
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Enable DEDICATED square drawing (completely separate from circle logic)
  const enableSquareDrawing = () => {
    console.log('⬜⬜⬜ ENABLESQUAREDRAWING FUNCTION CALLED - THIS SHOULD CREATE SQUARES ⬜⬜⬜')
    console.log('⬜ DEDICATED Square Drawing - Completely Separate from Circle Logic')
    
    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        console.log('🖱️ Map clicked for SQUARE creation. Checking for existing square...')
        
        // Use ref for immediate synchronous check (same as circle logic but separate)
        if (currentGeofenceRef.current) {
          // Square already exists - delete it on click
          console.log('🗑️ Deleting existing SQUARE on map click')
          currentGeofenceRef.current.setMap(null)
          currentGeofenceRef.current = null
          setEditingGeofenceOverlay(null)
          setHasCreatedGeofence(false)
          console.log('✅ Square deleted. Click again to create new square.')
          return
        }
        
        // First click - create SQUARE
        const center = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on clicked location
        mapInstanceRef.current!.setCenter(center)
        mapInstanceRef.current!.setZoom(15)  // Moderate zoom (not max)
        
        // Create SQUARE bounds (4 corners)
        const size = 0.001 // Small default size in degrees (~100m)
        
        console.log('⬜ Creating SQUARE bounds at:', center)
        
        // Create editable SQUARE using Rectangle (better for proportional resize)
        const rectangleBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(center.lat - size/2, center.lng - size/2), // SW corner
          new google.maps.LatLng(center.lat + size/2, center.lng + size/2)  // NE corner
        )
        
        console.log('⬜ Rectangle bounds created:', rectangleBounds)
        
        const squareRectangle = new google.maps.Rectangle({
          bounds: rectangleBounds,
          strokeColor: routeInfo.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: routeInfo.color + '40',
          fillOpacity: 0.3,
          editable: true, // User can resize by dragging corners - maintains rectangle shape
          draggable: true, // User can move entire shape
          zIndex: 1000
        })

        // Add listener to force square proportions (make rectangle square)
        let isUpdatingBounds = false // Flag to prevent infinite loop
        
        const maintainSquareShape = () => {
          if (isUpdatingBounds) return // Prevent recursive calls
          
          const bounds = squareRectangle.getBounds()
          if (bounds) {
            const center = bounds.getCenter()
            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()
            
            // Calculate the maximum distance to maintain square shape
            const latDiff = Math.abs(ne.lat() - sw.lat())
            const lngDiff = Math.abs(ne.lng() - sw.lng())
            const maxDiff = Math.max(latDiff, lngDiff)
            const halfSize = maxDiff / 2
            
            // Create perfect square bounds
            const newSquareBounds = new google.maps.LatLngBounds(
              new google.maps.LatLng(center.lat() - halfSize, center.lng() - halfSize), // SW
              new google.maps.LatLng(center.lat() + halfSize, center.lng() + halfSize)   // NE
            )
            
            // Check if bounds actually need updating (avoid unnecessary updates)
            const currentBounds = squareRectangle.getBounds()
            if (currentBounds && !currentBounds.equals(newSquareBounds)) {
              // Set flag to prevent recursive calls
              isUpdatingBounds = true
              
              // Update the rectangle to maintain square shape
              squareRectangle.setBounds(newSquareBounds)
              
              // Reset flag after a short delay
              setTimeout(() => {
                isUpdatingBounds = false
              }, 10)
            }
          }
        }

        // Listen for bounds changes to maintain square shape
        google.maps.event.addListener(squareRectangle, 'bounds_changed', maintainSquareShape)

        squareRectangle.setMap(mapInstanceRef.current)
        currentGeofenceRef.current = squareRectangle  // Store in ref for immediate access
        setEditingGeofenceOverlay(squareRectangle)  // Store for later saving
        setHasCreatedGeofence(true)  // Mark that geofence has been created
        
        console.log('⬜ Created editable SQUARE at clicked location:', center)
        console.log('🔍 Map zoomed to moderate level for editing')
        console.log('💡 SQUARE ready for editing. Click again to delete, or enter name and save.')
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Enable DEDICATED triangle drawing (completely separate from circle logic)
  const enableTriangleDrawing = () => {
    console.log('🔺 DEDICATED Triangle Drawing - Completely Separate from Circle Logic')
    
    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        console.log('🖱️ Map clicked for TRIANGLE creation. Checking for existing triangle...')
        
        // Use ref for immediate synchronous check
        if (currentGeofenceRef.current) {
          // Triangle already exists - delete it on click
          console.log('🗑️ Deleting existing TRIANGLE on map click')
          currentGeofenceRef.current.setMap(null)
          currentGeofenceRef.current = null
          setEditingGeofenceOverlay(null)
          setHasCreatedGeofence(false)
          console.log('✅ Triangle deleted. Click again to create new triangle.')
          return
        }
        
        // First click - create TRIANGLE
        const center = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on clicked location
        mapInstanceRef.current!.setCenter(center)
        mapInstanceRef.current!.setZoom(15)  // Moderate zoom (not max)
        
        // Create TRIANGLE bounds (3 corners)
        const size = 0.001 // Small default size in degrees (~100m)
        const triangleBounds = [
          { lat: center.lat + size/2, lng: center.lng },           // Top
          { lat: center.lat - size/2, lng: center.lng - size/2 },  // Bottom-left
          { lat: center.lat - size/2, lng: center.lng + size/2 }   // Bottom-right
        ]
        
        console.log('🔺 Creating TRIANGLE bounds at:', center)
        console.log('🔺 Triangle bounds created:', triangleBounds)
        
        // Create editable TRIANGLE polygon
        const trianglePolygon = new google.maps.Polygon({
          paths: triangleBounds,
          strokeColor: routeInfo.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: routeInfo.color + '40',
          fillOpacity: 0.3,
          editable: true, // User can resize by dragging vertices
          draggable: true, // User can move entire shape
          zIndex: 1000
        })

        trianglePolygon.setMap(mapInstanceRef.current)
        currentGeofenceRef.current = trianglePolygon  // Store in ref for immediate access
        setEditingGeofenceOverlay(trianglePolygon)  // Store for later saving
        setHasCreatedGeofence(true)  // Mark that geofence has been created
        
        console.log('🔺 Created editable TRIANGLE at clicked location:', center)
        console.log('🔍 Map zoomed to moderate level for editing')
        console.log('💡 TRIANGLE ready for editing. Click again to delete, or enter name and save.')
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Enable polygon drawing (for custom polygon only - NOT square or triangle)
  const enablePolygonDrawing = () => {
    console.log('📐 Enabling custom polygon drawing')
    enableCustomPolygonDrawing()
  }

  // Enable predefined shape drawing (square, triangle)
  const enablePredefinedShapeDrawing = () => {
    console.log(`📐 Enabling ${drawingType} single-click creation`)
    
    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        console.log('🖱️ Map clicked for shape creation. Checking for existing geofence...')
        
        // Use ref for immediate synchronous check (same as circle logic)
        if (currentGeofenceRef.current) {
          // Shape already exists - delete it on click
          console.log('🗑️ Deleting existing shape on map click')
          currentGeofenceRef.current.setMap(null)
          currentGeofenceRef.current = null
          setEditingGeofenceOverlay(null)
          setHasCreatedGeofence(false)
          console.log('✅ Shape deleted. Click again to create new shape.')
          return
        }
        
        // First click - create shape
        const center = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on clicked location
        mapInstanceRef.current!.setCenter(center)
        mapInstanceRef.current!.setZoom(15)  // Moderate zoom (not max)
        
        // Create default shape bounds
        const size = 0.001 // Small default size in degrees (~100m)
        let bounds: Array<{ lat: number, lng: number }> = []
        
        const shapeType = drawingType === 'trajectory' ? drawingType : selectedGeofenceType
        
        if (shapeType === 'square') {
          // Create square bounds around center
          console.log('⬜ Creating square bounds at:', center)
          bounds = [
            { lat: center.lat - size/2, lng: center.lng - size/2 }, // Bottom-left
            { lat: center.lat - size/2, lng: center.lng + size/2 }, // Bottom-right
            { lat: center.lat + size/2, lng: center.lng + size/2 }, // Top-right
            { lat: center.lat + size/2, lng: center.lng - size/2 }  // Top-left
          ]
          console.log('⬜ Square bounds created:', bounds)
        } else if (shapeType === 'triangle') {
          // Create triangle bounds around center
          bounds = [
            { lat: center.lat + size/2, lng: center.lng },           // Top
            { lat: center.lat - size/2, lng: center.lng - size/2 },  // Bottom-left
            { lat: center.lat - size/2, lng: center.lng + size/2 }   // Bottom-right
          ]
        }
        
        // Create editable polygon
        const polygon = new google.maps.Polygon({
          paths: bounds,
          strokeColor: routeInfo.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: routeInfo.color + '40',
          fillOpacity: 0.3,
          editable: true, // User can resize by dragging vertices
          draggable: true, // User can move entire shape
          zIndex: 1000
        })

        polygon.setMap(mapInstanceRef.current)
        currentGeofenceRef.current = polygon  // Store in ref for immediate access (same as circle)
        setEditingGeofenceOverlay(polygon)  // Store for later saving
        setHasCreatedGeofence(true)  // Mark that geofence has been created
        
        console.log(`📐 Created editable ${shapeType} at clicked location:`, center)
        console.log('🔍 Map zoomed to moderate level for editing')
        console.log('💡 Shape ready for editing. Click again to delete, or enter name and save.')
        
        // Don't auto-save, let user adjust and then save manually
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Enable custom polygon drawing (multiple clicks)
  const enableCustomPolygonDrawing = () => {
    console.log('📐 Enabling custom polygon drawing')

    let polygonPoints: Array<{ lat: number, lng: number }> = []
    // The map's own double-click zoom swallows the "finish" gesture, so the
    // shape is recorded from the Save button once three vertices exist.
    mapInstanceRef.current!.setOptions({ disableDoubleClickZoom: true })

    const finalize = () => {
      if (polygonPoints.length < 3 || currentGeofenceRef.current) return polygonPoints.length >= 3
      if (drawingPathRef.current) drawingPathRef.current.setMap(null)
      const finalPolygon = new google.maps.Polygon({
        paths: polygonPoints,
        strokeColor: routeInfo.color,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: routeInfo.color + '40',
        fillOpacity: 0.3,
        editable: true,
        draggable: true,
        zIndex: 1000
      })
      finalPolygon.setMap(mapInstanceRef.current)
      currentGeofenceRef.current = finalPolygon
      setEditingGeofenceOverlay(finalPolygon)
      setHasCreatedGeofence(true)
      drawingMarkersRef.current.forEach(marker => marker.setMap(null))
      drawingMarkersRef.current = []
      mapInstanceRef.current?.setOptions({ disableDoubleClickZoom: false })
      return true
    }
    finalizePolygonRef.current = finalize

    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (currentGeofenceRef.current) return
      if (event.latLng) {
        const point = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on first click (like other shapes)
        if (polygonPoints.length === 0) {
          console.log('🔍 First polygon point - zooming to clicked location')
          mapInstanceRef.current!.setCenter(point)
          mapInstanceRef.current!.setZoom(15)  // Moderate zoom (not max)
        }
        
        polygonPoints.push(point)
        
        // Create marker for each point (using symbol instead of image file)
        const marker = new google.maps.Marker({
          position: point,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: routeInfo.color,
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            strokeOpacity: 1
          },
          title: `Point ${polygonPoints.length}`,
          zIndex: 3000
        })

        drawingMarkersRef.current.push(marker)
        setDraftPolygonCount(polygonPoints.length)

        // Create/update polygon preview
        if (polygonPoints.length >= 2) {
          // Remove existing preview
          if (drawingPathRef.current) {
            drawingPathRef.current.setMap(null)
          }
          
          const polygon = new google.maps.Polygon({
            paths: polygonPoints,
            strokeColor: routeInfo.color,
            strokeOpacity: 0.8,
            strokeWeight: 3,
            fillColor: routeInfo.color + '40',
            fillOpacity: 0.3,
            zIndex: 1000
          })

          polygon.setMap(mapInstanceRef.current)
          drawingPathRef.current = polygon as any
        }
        
        console.log(`📐 Added polygon point ${polygonPoints.length}:`, point)
      }
    })

    // Double-click to finalize polygon shape (don't auto-save, let user save manually)
    const dblClickListener = mapInstanceRef.current!.addListener('dblclick', () => {
      if (finalize()) console.log('📐 Polygon closed by double-click, ready to save')
    })

    mapListenersRef.current.push(clickListener, dblClickListener)
  }

  // Enable DEDICATED point drawing (completely separate from other shapes)
  const enablePointDrawing = () => {
    console.log('📍 DEDICATED Point Drawing - Completely Separate from Other Shapes')
    
    const clickListener = mapInstanceRef.current!.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        console.log('🖱️ Map clicked for POINT creation. Checking for existing point...')
        
        // Use ref for immediate synchronous check (same as other shapes)
        if (currentGeofenceRef.current) {
          // Point already exists - delete it on click
          console.log('🗑️ Deleting existing POINT on map click')
          currentGeofenceRef.current.setMap(null)
          currentGeofenceRef.current = null
          setEditingGeofenceOverlay(null)
          setHasCreatedGeofence(false)
          console.log('✅ Point deleted. Click again to create new point.')
          return
        }
        
        // First click - create POINT
        const center = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        // Zoom to moderate level and center on clicked location
        mapInstanceRef.current!.setCenter(center)
        mapInstanceRef.current!.setZoom(15)  // Moderate zoom (not max)
        
        console.log('📍 Creating POINT marker at:', center)
        
        // Create point marker with colored circle icon (no external image needed)
        const pointMarker = new google.maps.Marker({
          position: center,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: routeInfo.color,
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            strokeOpacity: 1
          },
          title: 'Geofence Point',
          draggable: true, // User can move the point
          zIndex: 3000
        })

        currentGeofenceRef.current = pointMarker  // Store in ref for immediate access
        setEditingGeofenceOverlay(pointMarker)  // Store for later saving
        setHasCreatedGeofence(true)  // Mark that geofence has been created
        
        console.log('📍 Created editable POINT at clicked location:', center)
        console.log('🔍 Map zoomed to moderate level for editing')
        console.log('💡 POINT ready for editing. Click again to delete, or enter name and save.')
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Complete geofence drawing with validation and security
  const completeGeofenceDrawing = async (type: GeofenceType, geofenceData: any) => {
    if (!routeInfo.name.trim()) {
      if (showMessage) {
        showMessage('Please enter a name for the geofence', 'error')
      }
      return
    }

    console.log(`✅ ${type} geofence '${routeInfo.name}' completed`)
    
    try {
      // Prepare geofence payload with proper typing
      const payload: GeofencePayload & { customer_id?: number | null } = {
        name: routeInfo.name.trim(),
        description: `${type} geofence created on ${new Date().toLocaleDateString()}`,
        type: type,
        color: routeInfo.color,
        fillColor: routeInfo.color + '40',
        customer_id: drawCustomerId ? parseInt(drawCustomerId) : null,
        ...geofenceData
      }

      // Normalize polygon if needed
      if (payload.type === 'polygon' && payload.bounds) {
        payload.bounds = normalizePolygon(payload.bounds)
      }

      // Validate payload before sending
      const validation = validateGeofencePayload(payload)
      if (!validation.isValid) {
        console.error('❌ Geofence validation failed:', validation.errors)
        if (showMessage) {
          showMessage(`Validation failed: ${validation.errors.join(', ')}`, 'error')
        }
        return
      }

      console.log('💾 Saving validated geofence to database...', payload)
      
      // Show loading state
      if (showMessage) {
        showMessage('Saving geofence...', 'info')
      }

      // Save to database via API with proper error handling
      const response = await apiClient.post('/geofences', {
        ...payload,
        ...(initialVehicle?.id && initialVehicle.source ? {
          vehicle_alert_context: {
            auto_alerts: true,
            vehicle_source: initialVehicle.source,
            vehicle_id: Number(String(initialVehicle.id).replace(/^(ASSET|CONTAINER)-/i, ''))
          }
        } : {})
      })
      
      if (response.data.success) {
        console.log('✅ Geofence saved successfully!', response.data.geofence)
        if (showMessage) {
          const ruleCount = Array.isArray(response.data.alert_rules) ? response.data.alert_rules.length : 0
          showMessage(
            ruleCount > 0
              ? `Geofence '${payload.name}' and ${ruleCount} vehicle alerts created successfully!`
              : `Geofence '${payload.name}' saved successfully!`,
            'success'
          )
        }
        
        // Notify parent to refresh geofence list
        if (onDrawingComplete) {
          onDrawingComplete([]) // Empty points for geofences - triggers list refresh
        }
        
        clearDrawing()
      } else {
        throw new Error(response.data.message || 'Failed to save geofence')
      }
      
    } catch (error: any) {
      console.error('❌ Error saving geofence:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to save geofence'
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.'
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to create geofences.'
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please try again later.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      if (showMessage) {
        showMessage(errorMessage, 'error')
      }
    }
  }

  // Show name input validation message
  const showNameValidationMessage = () => {
    if (showMessage) {
      showMessage('Please enter a name for the geofence', 'warning')
    }
  }

  // Enable editing mode for existing corridor
  const enableEditingMode = () => {
    if (!mapInstanceRef.current || !editingCorridor) return

    console.log(`✏️ Enabling editing mode for corridor: ${editingCorridor.name}`)
    
    // Clear any existing editing
    clearEditing()
    
    // Set editing points from corridor
    setEditingPoints([...editingCorridor.points])
    
    // Show waypoint panel for editing
    setShowWaypointPanel(true)
    setRouteInfo({
      name: editingCorridor.name,
      color: editingCorridor.color,
      distance: 0,
      duration: 0,
      countries: []
    })
    
    // Change cursor to crosshair for adding points
    mapInstanceRef.current.setOptions({ 
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair'
    })

    // Create editable markers for existing points
    editingCorridor.points.forEach((point, index) => {
      const marker = new google.maps.Marker({
        position: point,
        map: mapInstanceRef.current,
        icon: {
          url: '/assets/placeholder.png',
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 32)
        },
        title: `Waypoint ${index + 1} - Drag to move, Click to delete`,
        zIndex: 3000,
        draggable: true
      })

      // Add drag listener to update point position
      marker.addListener('dragend', async () => {
        const newPosition = marker.getPosition()
        if (newPosition) {
          const updatedPoints = [...editingPoints]
          updatedPoints[index] = {
            lat: newPosition.lat(),
            lng: newPosition.lng(),
            address: point.address || `${newPosition.lat().toFixed(6)}, ${newPosition.lng().toFixed(6)}`
          }
          setEditingPoints(updatedPoints)
          updateRoadSnappedPath(updatedPoints)
          console.log(`📍 Moved waypoint ${index + 1} to: ${newPosition.lat()}, ${newPosition.lng()}`)
        }
      })

      // Add click listener to delete point
      marker.addListener('click', async () => {
        if (editingPoints.length > 2) {
          const confirmed = showConfirm ? await showConfirm(`Delete waypoint ${index + 1}?`) : confirm(`Delete waypoint ${index + 1}?`)
          if (confirmed) {
            removeEditingPoint(index)
          }
        } else {
          if (showMessage) {
            showMessage('A route must have at least 2 waypoints', 'warning')
          }
        }
      })

      editingMarkersRef.current.push(marker)
    })

    // Add click listener for adding new points
    const clickListener = mapInstanceRef.current.addListener('click', async (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        
        const address = await resolvePlaceName(point.lat, point.lng)
        addEditingPoint({ ...point, address: address || `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}` })
      }
    })

    mapListenersRef.current.push(clickListener)
  }

  // Add editing point
  const addEditingPoint = (point: { lat: number, lng: number, address?: string }) => {
    console.log(`📍 Adding editing point:`, point)
    
    setEditingPoints(prev => {
      const newPoints = [...prev, point]
      
      // Create editable marker
      const marker = new google.maps.Marker({
        position: point,
        map: mapInstanceRef.current,
        icon: {
          url: '/assets/placeholder.png',
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 32)
        },
        title: `Waypoint ${newPoints.length} - Drag to move, Click to delete`,
        zIndex: 3000,
        draggable: true
      })

      // Add drag listener
      marker.addListener('dragend', async () => {
        const newPosition = marker.getPosition()
        if (newPosition) {
          const updatedPoints = [...newPoints]
          updatedPoints[newPoints.length - 1] = {
            lat: newPosition.lat(),
            lng: newPosition.lng(),
            address: point.address || `${newPosition.lat().toFixed(6)}, ${newPosition.lng().toFixed(6)}`
          }
          setEditingPoints(updatedPoints)
          updateRoadSnappedPath(updatedPoints)
        }
      })

      // Add click listener to delete
      marker.addListener('click', async () => {
        if (newPoints.length > 2) {
          const confirmed = showConfirm ? await showConfirm(`Delete waypoint ${newPoints.length}?`) : confirm(`Delete waypoint ${newPoints.length}?`)
          if (confirmed) {
            removeEditingPoint(newPoints.length - 1)
          }
        } else {
          if (showMessage) {
            showMessage('A route must have at least 2 waypoints', 'warning')
          }
        }
      })

      editingMarkersRef.current.push(marker)
      updateRoadSnappedPath(newPoints)
      
      return newPoints
    })
  }

  // Remove editing point
  const removeEditingPoint = (index: number) => {
    setEditingPoints(prev => {
      const newPoints = prev.filter((_, i) => i !== index)
      updateRoadSnappedPath(newPoints)
      return newPoints
    })
    
    // Remove corresponding marker
    if (editingMarkersRef.current[index]) {
      editingMarkersRef.current[index].setMap(null)
      editingMarkersRef.current = editingMarkersRef.current.filter((_, i) => i !== index)
      
      // Update marker titles with new indices
      editingMarkersRef.current.forEach((marker, i) => {
        marker.setTitle(`Waypoint ${i + 1} - Drag to move, Click to delete`)
      })
    }
  }

  // Clear editing
  const clearEditing = () => {
    // Clear editing markers
    editingMarkersRef.current.forEach(marker => marker.setMap(null))
    editingMarkersRef.current = []
    
    // Clear editing points
    setEditingPoints([])
    
    // Reset cursor
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ 
        draggableCursor: '',
        draggingCursor: ''
      })
    }
    
    console.log('🧹 Editing cleared')
  }

  // Center map on selected geofence
  const centerMapOnGeofence = (geofence: Geofence) => {
    if (!mapInstanceRef.current) return
    
    console.log(`🎯 Centering map on geofence: ${geofence.name}`)
    
    if (geofence.type === 'circle' && geofence.center) {
      // Center on circle center
      mapInstanceRef.current.setCenter(geofence.center)
      mapInstanceRef.current.setZoom(15) // Good zoom level for geofences
      
    } else if (geofence.type === 'point' && geofence.center) {
      // Center on point center
      mapInstanceRef.current.setCenter(geofence.center)
      mapInstanceRef.current.setZoom(16) // Closer zoom for points
      
    } else if (geofence.bounds && geofence.bounds.length >= 3) {
      // Calculate bounds for polygon/square/triangle
      const latitudes = geofence.bounds.map(b => b.lat)
      const longitudes = geofence.bounds.map(b => b.lng)
      
      const bounds = new google.maps.LatLngBounds()
      geofence.bounds.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng))
      })
      
      // Fit the geofence in view with some padding
      mapInstanceRef.current.fitBounds(bounds, {
        top: 100,
        right: 100,
        bottom: 100,
        left: 100
      })
      
      // Ensure minimum zoom level for visibility
      google.maps.event.addListenerOnce(mapInstanceRef.current, 'bounds_changed', () => {
        const zoom = mapInstanceRef.current!.getZoom()
        if (zoom && zoom > 18) {
          mapInstanceRef.current!.setZoom(18) // Max zoom for geofences
        } else if (zoom && zoom < 12) {
          mapInstanceRef.current!.setZoom(12) // Min zoom for geofences
        }
      })
    }
    
    console.log(`✅ Map centered on geofence: ${geofence.name}`)
  }

  // Center map on selected corridor
  const centerMapOnCorridor = (corridor: Corridor) => {
    if (!mapInstanceRef.current || !corridor.points || corridor.points.length === 0) return

    console.log(`🎯 Centering map on corridor: ${corridor.name}`)

    if (corridor.points.length === 1) {
      // Single point - center on that point
      mapInstanceRef.current.setCenter(corridor.points[0])
      mapInstanceRef.current.setZoom(15)
    } else {
      // Multiple points - fit bounds to show entire route
      const bounds = new google.maps.LatLngBounds()
      
      corridor.points.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng))
      })
      
      // Add some padding around the route
      mapInstanceRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      })
      
      // Ensure minimum zoom level for visibility
      google.maps.event.addListenerOnce(mapInstanceRef.current, 'bounds_changed', () => {
        const zoom = mapInstanceRef.current!.getZoom()
        if (zoom && zoom > 16) {
          mapInstanceRef.current!.setZoom(16)
        }
      })
    }
  }

  // Add floating control buttons on the map near the geofence
  const addFloatingControlButtons = (overlay: google.maps.Circle | google.maps.Rectangle | google.maps.Polygon | google.maps.Marker) => {
    if (!mapInstanceRef.current) return
    
    // Get the center position of the shape
    let center: google.maps.LatLng | null = null
    
    if (overlay instanceof google.maps.Circle) {
      center = overlay.getCenter()
    } else if (overlay instanceof google.maps.Rectangle) {
      const bounds = overlay.getBounds()
      if (bounds) center = bounds.getCenter()
    } else if (overlay instanceof google.maps.Polygon) {
      const path = overlay.getPath()
      if (path && path.getLength() > 0) {
        // Calculate centroid of polygon
        let lat = 0, lng = 0
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i)
          lat += point.lat()
          lng += point.lng()
        }
        center = new google.maps.LatLng(lat / path.getLength(), lng / path.getLength())
      }
    } else if (overlay instanceof google.maps.Marker) {
      center = overlay.getPosition()
    }
    
    if (!center) return
    
    // Create floating control panel (hidden by default, shown on hover)
    const controlDiv = document.createElement('div')
    controlDiv.className = 'floating-geofence-controls'
    controlDiv.style.cssText = `
      position: absolute;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(4px);
      border-radius: 12px;
      padding: 6px;
      display: flex;
      gap: 3px;
      z-index: 1000;
      pointer-events: auto;
      opacity: 0;
      transition: all 0.3s ease;
      transform: translateY(10px);
    `
    
    // Create control buttons with standard black/white icons
    const buttons = [
      {
        icon: '⟷',
        title: 'Resize Libre/Contraint',
        action: () => {
          const isConstrained = overlay.get('constrainedResize')
          overlay.set('constrainedResize', !isConstrained)
          console.log(`🔄 Resize mode: ${!isConstrained ? 'Contraint' : 'Libre'}`)
        }
      },
      {
        icon: '✥',
        title: 'Déplacer ON/OFF',
        action: () => {
          const isDraggable = overlay.getDraggable()
          overlay.setDraggable(!isDraggable)
          console.log(`🚚 Move mode: ${!isDraggable ? 'ON' : 'OFF'}`)
        }
      },
      {
        icon: '✕',
        title: 'Supprimer',
        action: async () => {
          const confirmed = showConfirm ? await showConfirm(`Delete "${editingGeofence?.name}"?`) : confirm(`Delete "${editingGeofence?.name}"?`)
          if (editingGeofence && confirmed) {
            // Delete geofence
            fetch(`/api/geofences/${editingGeofence.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            })
            .then(response => response.json())
            .then(data => {
              if (data.success) {
                if (showMessage) {
                  showMessage('Geofence deleted!', 'success')
                }
                clearGeofenceEditing()
                setShowWaypointPanel(false)
                if (onDrawingComplete) onDrawingComplete([])
              }
            })
            .catch(error => console.error('Delete error:', error))
          }
        }
      },
      {
        icon: '↶',
        title: 'Retour',
        action: () => {
          clearGeofenceEditing()
          setShowWaypointPanel(false)
          if (onDrawingComplete) onDrawingComplete([])
        }
      }
    ]
    
    buttons.forEach(btn => {
      const button = document.createElement('button')
      button.innerHTML = btn.icon
      button.title = btn.title
      button.style.cssText = `
        width: 32px;
        height: 32px;
        border: 1px solid rgba(0, 0, 0, 0.3);
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.9);
        color: rgba(0, 0, 0, 0.8);
        font-size: 14px;
        font-weight: bold;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s ease;
        backdrop-filter: blur(8px);
        transform: scale(1);
        transform-origin: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      `
      button.addEventListener('mouseenter', () => {
        console.log(`🖱️ Hovering ${btn.icon} button - scaling up`)
        button.style.transform = 'scale(1.3)'
        button.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'
      })
      
      button.addEventListener('mouseleave', () => {
        console.log(`🖱️ Leaving ${btn.icon} button - scaling down`)
        button.style.transform = 'scale(1)'
        button.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
      })
      button.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        console.log(`🖱️ Clicked ${btn.icon} button - executing action`)
        btn.action()
      }
      controlDiv.appendChild(button)
    })
    
    // Create custom overlay to position the controls
    class FloatingControls extends google.maps.OverlayView {
      private div: HTMLElement
      public position: google.maps.LatLng
      
      constructor(position: google.maps.LatLng, content: HTMLElement) {
        super()
        this.position = position
        this.div = content
      }
      
      onAdd() {
        const panes = this.getPanes()
        if (panes) {
          panes.floatPane.appendChild(this.div)
        }
      }
      
      draw() {
        const projection = this.getProjection()
        if (projection) {
          const point = projection.fromLatLngToDivPixel(this.position)
          if (point) {
            this.div.style.left = (point.x - 80) + 'px' // Center horizontally
            this.div.style.top = (point.y + 30) + 'px'  // Position below shape
            
            // Scale buttons based on map zoom level
            const map = this.getMap()
            if (map) {
              const zoom = map.getZoom() || 10
              // Scale factor: zoom 10 = 1x, zoom 15 = 1.5x, zoom 20 = 2x, etc.
              const scaleFactor = Math.max(0.5, Math.min(2, zoom / 10))
              this.div.style.transform = `scale(${scaleFactor})`
              this.div.style.transformOrigin = 'center top'
            }
          }
        }
      }
      
      onRemove() {
        if (this.div.parentNode) {
          this.div.parentNode.removeChild(this.div)
        }
      }
    }
    
    const floatingControls = new FloatingControls(center, controlDiv)
    floatingControls.setMap(mapInstanceRef.current)
    
    // Function to update control position when geofence moves/resizes
    const updateControlPosition = () => {
      let newCenter: google.maps.LatLng | null = null
      
      if (overlay instanceof google.maps.Circle) {
        newCenter = overlay.getCenter()
      } else if (overlay instanceof google.maps.Rectangle) {
        const bounds = overlay.getBounds()
        if (bounds) newCenter = bounds.getCenter()
      } else if (overlay instanceof google.maps.Polygon) {
        const path = overlay.getPath()
        if (path && path.getLength() > 0) {
          let lat = 0, lng = 0
          for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i)
            lat += point.lat()
            lng += point.lng()
          }
          newCenter = new google.maps.LatLng(lat / path.getLength(), lng / path.getLength())
        }
      } else if (overlay instanceof google.maps.Marker) {
        newCenter = overlay.getPosition()
      }
      
      if (newCenter) {
        floatingControls.position = newCenter
        floatingControls.draw()
        console.log('📍 Updated floating controls position after geofence move/resize')
      }
    }
    
    // Add listeners to update control position when geofence changes
    if (overlay instanceof google.maps.Circle) {
      overlay.addListener('center_changed', updateControlPosition)
      overlay.addListener('radius_changed', updateControlPosition)
    } else if (overlay instanceof google.maps.Rectangle) {
      overlay.addListener('bounds_changed', updateControlPosition)
    } else if (overlay instanceof google.maps.Polygon) {
      overlay.getPath().addListener('set_at', updateControlPosition)
      overlay.getPath().addListener('insert_at', updateControlPosition)
      overlay.addListener('dragend', updateControlPosition)
    } else if (overlay instanceof google.maps.Marker) {
      overlay.addListener('position_changed', updateControlPosition)
      overlay.addListener('dragend', updateControlPosition)
    }
    
    // Add hover events to the overlay to show/hide controls
    const showControls = () => {
      controlDiv.style.opacity = '1'
      // Don't override the zoom-based transform, just adjust the translateY
      const currentTransform = controlDiv.style.transform
      if (currentTransform.includes('scale')) {
        controlDiv.style.transform = currentTransform.replace('translateY(10px)', 'translateY(0px)')
      } else {
        controlDiv.style.transform = 'translateY(0px)'
      }
    }
    
    const hideControls = () => {
      controlDiv.style.opacity = '0'
      // Don't override the zoom-based transform, just adjust the translateY
      const currentTransform = controlDiv.style.transform
      if (currentTransform.includes('scale')) {
        controlDiv.style.transform = currentTransform.replace('translateY(0px)', 'translateY(10px)')
      } else {
        controlDiv.style.transform = 'translateY(10px)'
      }
    }
    
    // Add zoom listener to update button sizes when map zoom changes
    const zoomListener = mapInstanceRef.current!.addListener('zoom_changed', () => {
      // The draw() method will automatically handle the zoom scaling
      floatingControls.draw()
      console.log(`🔍 Map zoom changed - updating button scale`)
    })
    
    // Add mouse events to the shape
    overlay.addListener('mouseover', showControls)
    overlay.addListener('mouseout', hideControls)
    
    // Also show controls when hovering over the control panel itself
    controlDiv.addEventListener('mouseenter', showControls)
    controlDiv.addEventListener('mouseleave', hideControls)
    
    // Store references to remove later
    overlay.set('floatingControls', floatingControls)
    overlay.set('zoomListener', zoomListener)
  }

  // Enable geofence editing mode
  const enableGeofenceEditingMode = () => {
    if (!mapInstanceRef.current || !editingGeofence) return

    console.log(`✏️ Enabling geofence editing mode: ${editingGeofence.name} (${editingGeofence.type})`)
    console.log('📋 Loading existing geofence parameters:', editingGeofence)
    
    // Clear any existing editing
    clearGeofenceEditing()
    
    // Load existing geofence parameters into state
    console.log('🔄 Setting selectedPlaceType to:', editingGeofence.placeType || 'custom')
    console.log('🔄 Setting selectedPurpose to:', editingGeofence.purpose || 'tracking')
    console.log('🔄 Setting routeInfo.name to:', editingGeofence.name)
    setSelectedPlaceType(editingGeofence.placeType || 'custom')
    setSelectedPurpose(editingGeofence.purpose || 'tracking')
    
    // Set the geofence name and color in the form
    setRouteInfo(prev => ({
      ...prev,
      name: editingGeofence.name || '',
      color: editingGeofence.color || '#3b82f6'
    }))
    setGeofenceParameters({
      alertOnEntry: editingGeofence.alertOnEntry || false,
      alertOnExit: editingGeofence.alertOnExit || false,
      alertMessage: editingGeofence.parameters?.alertMessage || '',
      speedLimit: editingGeofence.parameters?.speedLimit,
      speedAlert: editingGeofence.parameters?.speedAlert || false,
      authorizationLevel: editingGeofence.parameters?.authorizationLevel || 'public',
      allowedVehicleTypes: editingGeofence.parameters?.allowedVehicleTypes || [],
      requiredPermits: editingGeofence.parameters?.requiredPermits || [],
      timeRestrictions: editingGeofence.parameters?.timeRestrictions || {
        allowedHours: [],
        allowedDays: []
      },
      dwellTimeLimit: editingGeofence.parameters?.dwellTimeLimit,
      customProperties: editingGeofence.parameters?.customProperties || {}
    })
    
    // Set the geofence type for editing
    setSelectedGeofenceType(editingGeofence.type || 'polygon')
    console.log('🔄 Setting selectedGeofenceType to:', editingGeofence.type || 'polygon')
    
    // IMPORTANT: Set drawingType to 'geofence' so Place Type and Purpose selectors are visible
    // The condition {drawingType !== 'trajectory'} controls their visibility
    console.log('🔄 Setting drawingType to geofence (was trajectory) to show Place Type/Purpose selectors')
    
    // Show panel for geofence editing
    setShowWaypointPanel(true)
    
    // Change cursor to crosshair for geofence editing
    mapInstanceRef.current.setOptions({ 
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair'
    })

    // Create editable geofence overlay
    if (editingGeofence.type === 'circle' && editingGeofence.center && editingGeofence.radius) {
      const circle = new google.maps.Circle({
        center: editingGeofence.center,
        radius: editingGeofence.radius,
        strokeColor: editingGeofence.color,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: editingGeofence.fillColor,
        fillOpacity: 0.3,
        editable: true, // Make it editable
        draggable: true, // Make it draggable
        zIndex: 1000
      })

      circle.setMap(mapInstanceRef.current)
      setEditingGeofenceOverlay(circle)
      
      // Add floating control buttons on the map near the shape
      addFloatingControlButtons(circle)

      // Add listeners for editing
      circle.addListener('center_changed', () => {
        console.log('📍 Geofence center moved')
      })

      circle.addListener('radius_changed', () => {
        console.log('📏 Geofence radius changed')
      })

    } else if (editingGeofence.bounds && editingGeofence.bounds.length >= 3) {
      console.log(`✏️ Creating editing overlay for ${editingGeofence.type} geofence`)
      
      // Handle different geofence types appropriately
      if (editingGeofence.type === 'square' && editingGeofence.bounds.length === 4) {
        console.log('⬜ Editing SQUARE geofence - using Rectangle for proper proportional resizing')
        
        // Convert bounds array to LatLngBounds for Rectangle
        const bounds = editingGeofence.bounds
        const latitudes = bounds.map(b => b.lat)
        const longitudes = bounds.map(b => b.lng)
        
        const rectangleBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(Math.min(...latitudes), Math.min(...longitudes)), // SW
          new google.maps.LatLng(Math.max(...latitudes), Math.max(...longitudes))  // NE
        )
        
        const rectangle = new google.maps.Rectangle({
          bounds: rectangleBounds,
          strokeColor: editingGeofence.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: editingGeofence.fillColor,
          fillOpacity: 0.3,
          editable: true, // User can resize by dragging corners - maintains rectangle shape
          draggable: true, // User can move entire shape
          zIndex: 1000
        })

        rectangle.setMap(mapInstanceRef.current)
        setEditingGeofenceOverlay(rectangle)
        
        // Set initial constraint mode (can be toggled by user)
        rectangle.set('constrainedResize', true)
        
        // Add floating control buttons on the map near the shape
        addFloatingControlButtons(rectangle)
        
        // Add listener to optionally maintain square proportions during editing
        let isUpdatingBounds = false
        const maintainSquareShape = () => {
          if (isUpdatingBounds) return
          
          // Check if constrained resize is enabled
          const isConstrained = rectangle.get('constrainedResize')
          if (!isConstrained) {
            console.log('🔓 Free resize mode - no shape constraints')
            return // Allow free resizing
          }
          
          const bounds = rectangle.getBounds()
          if (bounds) {
            const center = bounds.getCenter()
            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()
            const latDiff = Math.abs(ne.lat() - sw.lat())
            const lngDiff = Math.abs(ne.lng() - sw.lng())
            const maxDiff = Math.max(latDiff, lngDiff)
            const halfSize = maxDiff / 2
            
            const newSquareBounds = new google.maps.LatLngBounds(
              new google.maps.LatLng(center.lat() - halfSize, center.lng() - halfSize),
              new google.maps.LatLng(center.lat() + halfSize, center.lng() + halfSize)
            )
            
            const currentBounds = rectangle.getBounds()
            if (currentBounds && !currentBounds.equals(newSquareBounds)) {
              isUpdatingBounds = true
              rectangle.setBounds(newSquareBounds)
              setTimeout(() => { isUpdatingBounds = false }, 10)
            }
          }
        }
        
        rectangle.addListener('bounds_changed', maintainSquareShape)
        console.log('⬜ Square editing mode enabled with toggleable proportional resizing')
        
      } else {
        console.log(`📐 Editing ${editingGeofence.type} geofence - using Polygon for vertex editing`)
        
        // Use Polygon for triangle, polygon, and other complex shapes
        const polygon = new google.maps.Polygon({
          paths: editingGeofence.bounds,
          strokeColor: editingGeofence.color,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: editingGeofence.fillColor,
          fillOpacity: 0.3,
          editable: true, // Allow vertex editing for complex shapes
          draggable: true, // Allow moving entire shape
          zIndex: 1000
        })

        polygon.setMap(mapInstanceRef.current)
        setEditingGeofenceOverlay(polygon)
        
        // Add floating control buttons on the map near the shape
        addFloatingControlButtons(polygon)

        // Add listeners for editing
        polygon.getPath().addListener('set_at', () => {
          console.log('📐 Geofence vertex moved')
        })
        
        polygon.getPath().addListener('insert_at', () => {
          console.log('📐 Geofence vertex added')
        })
        
        console.log(`📐 ${editingGeofence.type} editing mode enabled with vertex manipulation`)
      }
    }
  }

  // Clear geofence editing
  const clearGeofenceEditing = () => {
    // Clear editing overlay and floating controls
    if (editingGeofenceOverlay) {
      // Remove floating controls if they exist
      const floatingControls = editingGeofenceOverlay.get('floatingControls')
      if (floatingControls) {
        floatingControls.setMap(null)
      }
      
      // Remove zoom listener if it exists
      const zoomListener = editingGeofenceOverlay.get('zoomListener')
      if (zoomListener) {
        google.maps.event.removeListener(zoomListener)
      }
      
      editingGeofenceOverlay.setMap(null)
      setEditingGeofenceOverlay(null)
    }
    
    // Reset geofence creation flag and ref
    setHasCreatedGeofence(false)
    currentGeofenceRef.current = null
    
    // Reset cursor
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ 
        draggableCursor: '',
        draggingCursor: ''
      })
    }
    
    console.log('🧹 Geofence editing cleared')
  }

  // Save geofence editing changes
  const saveGeofenceEditingChanges = () => {
    if (editingGeofence && editingGeofenceOverlay && onGeofenceUpdate) {
      console.log(`💾 Saving changes to geofence: ${editingGeofence.id}`)
      
      let geofenceData: any = {
        name: routeInfo.name,
        color: routeInfo.color,
        // Enhanced parameters
        placeType: selectedPlaceType,
        purpose: selectedPurpose,
        parameters: geofenceParameters
      }

      if (editingGeofence.type === 'circle' && editingGeofenceOverlay instanceof google.maps.Circle) {
        const center = editingGeofenceOverlay.getCenter()
        const radius = editingGeofenceOverlay.getRadius()
        
        geofenceData = {
          ...geofenceData,
          type: 'circle',
          center: center ? { lat: center.lat(), lng: center.lng() } : editingGeofence.center,
          radius: radius
        }
      } else if (editingGeofenceOverlay instanceof google.maps.Rectangle) {
        console.log('⬜ Saving Rectangle (square) geofence changes')
        const rectBounds = editingGeofenceOverlay.getBounds()
        
        if (rectBounds) {
          const ne = rectBounds.getNorthEast()
          const sw = rectBounds.getSouthWest()
          const bounds: Array<{ lat: number, lng: number }> = [
            { lat: sw.lat(), lng: sw.lng() }, // Bottom-left
            { lat: sw.lat(), lng: ne.lng() }, // Bottom-right
            { lat: ne.lat(), lng: ne.lng() }, // Top-right
            { lat: ne.lat(), lng: sw.lng() }  // Top-left
          ]
          
          geofenceData = {
            ...geofenceData,
            type: 'square',
            bounds: bounds
          }
        }
      } else if (editingGeofenceOverlay instanceof google.maps.Polygon) {
        console.log('📐 Saving Polygon geofence changes')
        const path = editingGeofenceOverlay.getPath()
        const bounds: Array<{ lat: number, lng: number }> = []
        
        path.forEach((latLng: google.maps.LatLng) => {
          bounds.push({ lat: latLng.lat(), lng: latLng.lng() })
        })
        
        geofenceData = {
          ...geofenceData,
          type: editingGeofence.type,
          bounds: bounds
        }
      }

      onGeofenceUpdate(editingGeofence.id, geofenceData)
      clearGeofenceEditing()
      setShowWaypointPanel(false)
    }
  }

  // Save editing changes
  const saveEditingChanges = () => {
    if (editingCorridor && editingPoints.length >= 2 && onCorridorUpdate) {
      console.log(`💾 Saving changes to corridor: ${editingCorridor.id}`)
      onCorridorUpdate(editingCorridor.id, editingPoints)
      clearEditing()
      setShowWaypointPanel(false)
    }
  }

  // Add drawing point with road snapping
  const addDrawingPoint = (point: { lat: number, lng: number, address?: string }) => {
    console.log(`📍 Adding corridor point:`, point)
    
    setDrawingPoints(prev => {
      const newPoints = [...prev, point]
      
      // Create waypoint marker using red placeholder
      const marker = new google.maps.Marker({
        position: point,
        map: mapInstanceRef.current,
        icon: {
          url: '/assets/placeholder.png',
          scaledSize: new google.maps.Size(28, 28),
          anchor: new google.maps.Point(14, 28)
        },
        title: `Waypoint ${newPoints.length} - Drag to adjust route`,
        zIndex: 3000,
        draggable: true // Allow users to adjust waypoints
      })

      // Add drag listener to update route when waypoint is moved
      marker.addListener('dragend', () => {
        const newPosition = marker.getPosition()
        if (newPosition) {
          const updatedPoints = [...newPoints]
          updatedPoints[newPoints.length - 1] = {
            lat: newPosition.lat(),
            lng: newPosition.lng()
          }
          setDrawingPoints(updatedPoints)
          updateRoadSnappedPath(updatedPoints)
        }
      })

      waypointMarkersRef.current.push(marker)
      
      // Update road-snapped path
      updateRoadSnappedPath(newPoints)
      
      return newPoints
    })
  }

  // Remove a drawing point
  const removeDrawingPoint = (index: number) => {
    setDrawingPoints(prev => {
      const newPoints = prev.filter((_, i) => i !== index)
      updateRoadSnappedPath(newPoints)
      return newPoints
    })
    
    // Remove corresponding marker
    if (waypointMarkersRef.current[index]) {
      waypointMarkersRef.current[index].setMap(null)
      waypointMarkersRef.current = waypointMarkersRef.current.filter((_, i) => i !== index)
    }
  }

  // Update road-snapped path using Directions API
  const updateRoadSnappedPath = async (points: Array<{ lat: number, lng: number, address?: string }>) => {
    if (!mapInstanceRef.current || !directionsServiceRef.current || !directionsRendererRef.current) return
    if (points.length < 2) return

    try {
      console.log(`🛣️ Creating road-snapped route with ${points.length} points`)

      // Prepare waypoints for Directions API (max 25 waypoints limit)
      const origin = points[0]
      const destination = points[points.length - 1]
      const middlePoints = points.slice(1, -1)
      
      // Limit waypoints to 23 (25 - origin - destination = 23)
      const limitedWaypoints = middlePoints.length > 23 
        ? middlePoints.filter((_, index) => index % Math.ceil(middlePoints.length / 23) === 0).slice(0, 23)
        : middlePoints
      
      const waypoints = limitedWaypoints.map(point => ({
        location: new google.maps.LatLng(point.lat, point.lng),
        stopover: true
      }))
      
      if (middlePoints.length > 23) {
        console.log(`⚠️ Reduced waypoints from ${middlePoints.length} to ${limitedWaypoints.length} due to Google Maps limit`)
      }

      // Request directions following roads
      const request: google.maps.DirectionsRequest = {
        origin: new google.maps.LatLng(origin.lat, origin.lng),
        destination: new google.maps.LatLng(destination.lat, destination.lng),
        waypoints: waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Keep waypoints in order
        avoidHighways: false,
        avoidTolls: false
      }

      directionsServiceRef.current.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          console.log('✅ Road-snapped route calculated')
          
          // Extract route information
          const route = result.routes[0]
          const leg = route.legs[0]
          
          // Calculate total distance and duration
          let totalDistance = 0
          let totalDuration = 0
          route.legs.forEach(leg => {
            totalDistance += leg.distance?.value || 0
            totalDuration += leg.duration?.value || 0
          })
          
          // Extract countries from route (basic implementation)
          const countries = new Set<string>()
          route.legs.forEach(leg => {
            leg.steps.forEach(step => {
              // Try to extract country from instructions or address
              const instruction = step.instructions
              if (instruction.includes('Mali')) countries.add('Mali')
              if (instruction.includes('Burkina')) countries.add('Burkina Faso')
              if (instruction.includes('Senegal')) countries.add('Senegal')
              if (instruction.includes('Guinea')) countries.add('Guinea')
            })
          })
          
          // Update route info
          setRouteInfo(prev => ({
            ...prev,
            distance: totalDistance,
            duration: totalDuration,
            countries: Array.from(countries)
          }))
          
          // Display the road-following route
          directionsRendererRef.current!.setDirections(result)
          
          // Update the route color and style
          directionsRendererRef.current!.setOptions({
            polylineOptions: {
              strokeColor: routeInfo.color,
              strokeOpacity: 0.8,
              strokeWeight: 5,
              zIndex: 2500
            }
          })
        } else {
          console.warn('⚠️ Directions request failed:', status)
          // Fallback to straight line if directions fail
          updateStraightLinePath(points)
        }
      })
    } catch (error) {
      console.error('❌ Error creating road-snapped path:', error)
      // Fallback to straight line
      updateStraightLinePath(points)
    }
  }

  // Fallback: Update with straight line path
  const updateStraightLinePath = (points: Array<{ lat: number, lng: number }>) => {
    if (!mapInstanceRef.current) return

    // Remove existing path
    if (drawingPathRef.current) {
      drawingPathRef.current.setMap(null)
    }

    // Create straight line path as fallback
    if (points.length >= 2) {
      const newPath = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#F59E0B', // Orange for fallback
        strokeOpacity: 0.8,
        strokeWeight: 5,
        zIndex: 2500,
        strokePattern: [10, 5] // Dashed line to indicate it's not road-snapped
      })

      newPath.setMap(mapInstanceRef.current)
      drawingPathRef.current = newPath
    }
  }

  // Clear drawing
  const clearDrawing = () => {
    // Clear waypoint markers
    waypointMarkersRef.current.forEach(marker => marker.setMap(null))
    waypointMarkersRef.current = []
    
    // Clear drawing markers
    drawingMarkersRef.current.forEach(marker => marker.setMap(null))
    drawingMarkersRef.current = []
    
    // Clear directions renderer
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections({ routes: [] } as any)
    }
    
    // Clear fallback path
    if (drawingPathRef.current) {
      drawingPathRef.current.setMap(null)
      drawingPathRef.current = null
    }
    
    // Clear points
    setDrawingPoints([])
    
    // Reset geofence creation flag and ref
    setHasCreatedGeofence(false)
    setDraftPolygonCount(0)
    finalizePolygonRef.current = null
    currentGeofenceRef.current = null
    mapInstanceRef.current?.setOptions({ disableDoubleClickZoom: false })

    // Hide waypoint panel
    setShowWaypointPanel(false)
    
    // Reset route info
    setRouteInfo({
      name: '',
      color: '#10B981',
      distance: 0,
      duration: 0,
      countries: []
    })
    
    // Reset cursor
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ 
        draggableCursor: '',
        draggingCursor: ''
      })
    }
    
    console.log('🧹 Drawing cleared')
  }

  // Complete drawing and save to database
  const completeDrawing = async () => {
    if (drawingPoints.length >= 2 && routeInfo.name.trim()) {
      console.log(`✅ Route '${routeInfo.name}' completed with ${drawingPoints.length} points`)
      
      try {
        // Prepare data for API
        const corridorData = {
          name: routeInfo.name,
          description: `Route with ${drawingPoints.length} waypoints. Distance: ${(routeInfo.distance / 1000).toFixed(1)}km, Time: ${Math.round(routeInfo.duration / 60)}min`,
          color: routeInfo.color,
          width: 5,
          waypoints: drawingPoints.map(point => ({
            lat: point.lat,
            lng: point.lng,
            address: point.address || ''
          })),
          // 🆕 NEW: Add route statistics
          totalDistance: routeInfo.distance,
          totalDuration: routeInfo.duration,
          countries: routeInfo.countries,
          customer_id: drawCustomerId ? parseInt(drawCustomerId) : null,
          ...(initialVehicle?.id && initialVehicle.source ? {
            vehicle_alert_context: {
              auto_alerts: true,
              vehicle_source: initialVehicle.source,
              vehicle_id: Number(String(initialVehicle.id).replace(/^(ASSET|CONTAINER)-/i, ''))
            }
          } : {})
        }
        
        console.log('💾 Saving corridor to database...', corridorData)
        
        // Save to database via API
        const response = await apiClient.post('/corridors', corridorData)
        
        if (response.data.success) {
          console.log('✅ Corridor saved successfully!')
          
          // Clear drawing first
          clearDrawing()
          
          // Notify parent to refresh corridor list
          if (onDrawingComplete) {
            onDrawingComplete(drawingPoints)
          }
          
          // Show success message
          if (showMessage) {
            const ruleCount = Array.isArray(response.data.alert_rules) ? response.data.alert_rules.length : 0
            showMessage(
              `Route '${routeInfo.name}' saved successfully!${ruleCount > 0 ? ' Vehicle deviation alert created.' : ''} Distance: ${(routeInfo.distance / 1000).toFixed(1)} km, Time: ${Math.round(routeInfo.duration / 60)} min`,
              'success'
            )
          }
        } else {
          throw new Error(response.data.message || 'Failed to save corridor')
        }
        
      } catch (error) {
        console.error('❌ Error saving corridor:', error)
        if (showMessage) {
          showMessage(`Error saving route: ${error.message || error}`, 'error')
        }
      }
    } else {
      if (!routeInfo.name.trim()) {
        if (showMessage) {
          showMessage('Please enter a name for the route', 'warning')
        }
      } else {
        if (showMessage) {
          showMessage('Please add at least 2 points to create a route', 'warning')
        }
      }
    }
  }

  // Initialize map on mount
  useEffect(() => {
    initializeMap()
    
    // Cleanup on unmount
    return () => {
      mapListenersRef.current.forEach(listener => {
        google.maps.event.removeListener(listener)
      })
      referenceTrajectoryRef.current?.setMap(null)
      referenceVehicleMarkerRef.current?.setMap(null)
    }
  }, [initializeMap])

  // Update displays when data changes
  // Since only selected items are displayed, no need for highlighting functions
  // The display functions already handle the selected state styling

  useEffect(() => {
    console.log('🔍 Corridor useEffect triggered:', {
      hasMap: !!mapInstanceRef.current,
      selectedCorridor: selectedCorridor?.name,
      editingCorridor: editingCorridor?.name,
      totalCorridors: corridors.length
    })
    
    if (mapInstanceRef.current) {
      displayCorridors()
      
      // Center map on selected corridor or reset to default view
      if (selectedCorridor) {
        console.log('📍 Centering on selected corridor:', selectedCorridor.name)
        centerMapOnCorridor(selectedCorridor)
      } else if (!editingCorridor) {
        // Reset to default view when no corridor selected
        console.log('🔄 Resetting map to default view - no corridor selected')
        mapInstanceRef.current.setCenter({ lat: 12.6392, lng: -8.0029 }) // Bamako, Mali
        mapInstanceRef.current.setZoom(8) // Wide view
      }
    } else {
      console.warn('⚠️ Map instance not ready when corridor useEffect triggered')
    }
  }, [corridors, selectedCorridor, editingCorridor]) // Reload when data or selection changes

  useEffect(() => {
    console.log('🔍 Geofence useEffect triggered:', {
      hasMap: !!mapInstanceRef.current,
      selectedGeofence: selectedGeofence?.name,
      editingGeofence: editingGeofence?.name,
      totalGeofences: geofences.length,
      centerOnGeofence
    })
    
    if (mapInstanceRef.current) {
      displayGeofences()
      
      // Center map on selected geofence (when centerOnGeofence trigger is set)
      if (selectedGeofence && centerOnGeofence) {
        console.log('📍 Centering on selected geofence:', selectedGeofence.name)
        centerMapOnGeofence(selectedGeofence)
      } else if (!selectedGeofence && !editingGeofence) {
        // Reset to default view when no geofence selected
        console.log('🔄 Resetting map to default view - no geofence selected')
        mapInstanceRef.current.setCenter({ lat: 12.6392, lng: -8.0029 }) // Bamako, Mali
        mapInstanceRef.current.setZoom(8) // Wide view
      }
    } else {
      console.warn('⚠️ Map instance not ready when geofence useEffect triggered')
    }
  }, [geofences, selectedGeofence, editingGeofence, centerOnGeofence]) // Reload when data or selection changes

  // Clear any existing geofence shape (to ensure only one shape per session)
  const clearExistingGeofenceShape = () => {
    console.log('🧹 Clearing existing geofence shape before creating new one')
    
    // Clear current shape using ref (synchronous)
    if (currentGeofenceRef.current) {
      console.log('🗑️ Removing existing shape from map')
      currentGeofenceRef.current.setMap(null)
      currentGeofenceRef.current = null
    }
    
    // Clear state
    setEditingGeofenceOverlay(null)
    setHasCreatedGeofence(false)
    
    console.log('✅ Existing shape cleared - ready for new shape creation')
  }

  // Handle drawing mode changes
  useEffect(() => {
    if (isDrawingMode && mapInstanceRef.current) {
      console.log('🔄 useEffect triggered - re-enabling drawing mode')
      console.log('🔍 Current selectedGeofenceType:', selectedGeofenceType)
      
      // Clear any existing shape before enabling new drawing mode
      clearExistingGeofenceShape()
      
      enableDrawingMode()
    } else if (!isDrawingMode) {
      clearDrawing()
    }
  }, [isDrawingMode, drawingType, selectedGeofenceType, initialDrawingRequestId])  // Added selectedGeofenceType to dependencies

  // Handle corridor editing mode changes
  useEffect(() => {
    if (isEditingMode && editingCorridor && mapInstanceRef.current) {
      enableEditingMode()
    } else if (!isEditingMode) {
      clearEditing()
    }
  }, [isEditingMode, editingCorridor])

  // Handle geofence editing mode changes
  useEffect(() => {
    console.log('🔄 Geofence editing useEffect triggered:', {
      isEditingGeofence,
      editingGeofence: editingGeofence?.name,
      hasMap: !!mapInstanceRef.current
    })
    
    if (isEditingGeofence && editingGeofence && mapInstanceRef.current) {
      console.log('✅ Calling enableGeofenceEditingMode()')
      enableGeofenceEditingMode()
    } else if (!isEditingGeofence) {
      console.log('❌ Clearing geofence editing')
      clearGeofenceEditing()
    } else {
      console.log('⚠️ Conditions not met for geofence editing:', {
        isEditingGeofence,
        hasEditingGeofence: !!editingGeofence,
        hasMap: !!mapInstanceRef.current
      })
    }
  }, [isEditingGeofence, editingGeofence])

  // Center map on selected corridor
  useEffect(() => {
    if (selectedCorridor && mapInstanceRef.current && centerOnCorridor) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        centerMapOnCorridor(selectedCorridor)
      }, 300)
    }
  }, [selectedCorridor, centerOnCorridor])

  return (
    <div className="h-full flex flex-col bg-gray-50 relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <div className="text-gray-600 text-sm">Loading Corridor Map...</div>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 bg-red-50 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="text-red-600 font-medium mb-2">Error loading map</div>
            <div className="text-red-500 text-sm">{error}</div>
          </div>
        </div>
      )}


      {/* Route Information Panel */}
      {showWaypointPanel && (
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200 p-4 z-40 w-96">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-semibold text-gray-800">
              {isEditingGeofence ? '✏️ Edit Geofence' : 
               isEditingMode ? '✏️ Edit Route' : 
               drawingType === 'trajectory' ? '🛣️ New Route' : 
               '📍 New Geofence'}
            </div>
            <button
              onClick={() => {
                console.log('❌ Closing panel - resetting drawing mode')
                setShowWaypointPanel(false)
                clearDrawing()
                clearGeofenceEditing()
                
                // Notify parent to reset drawing mode
                if (onDrawingComplete) {
                  onDrawingComplete([]) // Empty array signals cancellation
                }
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>

          {/* Name and Color */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isEditingGeofence ? 'Nom du Geofence' : 
               drawingType === 'trajectory' ? 'Nom du Route' : 
               'Nom du Geofence'}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={routeInfo.name}
                onChange={(e) => setRouteInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder={isEditingGeofence ? 'Entrez le nom du geofence' : 
                            drawingType === 'trajectory' ? 'Entrez le nom du corridor' : 
                            'Entrez le nom du geofence'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <input
                type="color"
                value={routeInfo.color}
                onChange={(e) => setRouteInfo(prev => ({ ...prev, color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                title="Couleur du route"
              />
            </div>
            {authCustomerId == null && !isEditingGeofence && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                <select
                  value={drawCustomerId}
                  onChange={(e) => setDrawCustomerId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Partagé (tous les clients)</option>
                  {customerOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name} ({c.customer_code})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Geofence Type Selector */}
          {drawingType !== 'trajectory' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de Geofence
              </label>
              {isEditingGeofence ? (
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    ✏️ Mode édition: Vous modifiez un geofence existant. Les boutons de forme sont désactivés.
                  </div>
                  
                  {/* Current Properties Display (for reference) */}
                  {editingGeofence && (
                    <div className="p-3 bg-blue-50 rounded-lg text-sm mb-3">
                      <div className="font-medium text-blue-800 mb-2">📋 Current: {editingGeofence.placeType || 'custom'} - {editingGeofence.purpose || 'tracking'}</div>
                    </div>
                  )}

                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                  <button
                  onClick={() => {
                    // Warn if switching shapes with existing shape
                    if (currentGeofenceRef.current && selectedGeofenceType !== 'circle') {
                      console.log('⚠️ Switching from', selectedGeofenceType, 'to circle - existing shape will be cleared')
                    }
                    setSelectedGeofenceType('circle')
                    console.log('🔴 Circle selected for drawing')
                  }}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedGeofenceType === 'circle'
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>⭕</span>
                  <span>Cercle</span>
                </button>
                  <button
                  onClick={() => {
                    // Warn if switching shapes with existing shape
                    if (currentGeofenceRef.current && selectedGeofenceType !== 'square') {
                      console.log('⚠️ Switching from', selectedGeofenceType, 'to square - existing shape will be cleared')
                    }
                    console.log('🟠 SQUARE BUTTON CLICKED - Before setState')
                    setSelectedGeofenceType('square')
                    console.log('🟠 Square selected for drawing - selectedGeofenceType set to: square')
                    console.log('🔍 useEffect will automatically re-enable drawing mode with new type')
                  }}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedGeofenceType === 'square'
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>⬜</span>
                  <span>Carré</span>
                </button>
                  <button
                  onClick={() => {
                    // Warn if switching shapes with existing shape
                    if (currentGeofenceRef.current && selectedGeofenceType !== 'triangle') {
                      console.log('⚠️ Switching from', selectedGeofenceType, 'to triangle - existing shape will be cleared')
                    }
                    setSelectedGeofenceType('triangle')
                    console.log('🟣 Triangle selected for drawing')
                  }}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedGeofenceType === 'triangle'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>🔺</span>
                  <span>Triangle</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                  onClick={() => {
                    // Warn if switching shapes with existing shape
                    if (currentGeofenceRef.current && selectedGeofenceType !== 'polygon') {
                      console.log('⚠️ Switching from', selectedGeofenceType, 'to polygon - existing shape will be cleared')
                    }
                    setSelectedGeofenceType('polygon')
                    console.log('🔷 Polygon selected for drawing')
                  }}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedGeofenceType === 'polygon'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>🔷</span>
                  <span>Polygone</span>
                </button>
                  <button
                  onClick={() => {
                    // Warn if switching shapes with existing shape
                    if (currentGeofenceRef.current && selectedGeofenceType !== 'point') {
                      console.log('⚠️ Switching from', selectedGeofenceType, 'to point - existing shape will be cleared')
                    }
                    setSelectedGeofenceType('point')
                    console.log('📍 Point selected for drawing')
                  }}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedGeofenceType === 'point'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>📍</span>
                  <span>Point</span>
                </button>
              </div>
                </>
              )}
            </div>
          )}


          {/* Waypoints/Points List */}
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {isEditingGeofence ? '📍 Geofence Properties' :
               drawingType === 'trajectory' ? `📍 Points de Route (${isEditingMode ? editingPoints.length : drawingPoints.length})` :
               `📍 Geofence ${selectedGeofenceType.charAt(0).toUpperCase() + selectedGeofenceType.slice(1)}`}
            </div>
            
            {/* Show waypoint list for trajectory mode */}
            {drawingType === 'trajectory' && (
              <>
                {(isEditingMode ? editingPoints : drawingPoints).length === 0 ? (
                  <div className="text-sm text-gray-600 italic p-3 bg-gray-50 rounded-lg">
                    Cliquez sur la carte pour ajouter des points...
                  </div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {(isEditingMode ? editingPoints : drawingPoints).map((point, index) => (
                      <div key={index} className="flex items-start space-x-3 p-2 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">
                            {point.address || 'Adresse inconnue'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (isEditingMode) {
                              removeEditingPoint(index)
                            } else {
                              removeDrawingPoint(index)
                            }
                          }}
                          className="flex-shrink-0 text-red-500 hover:text-red-700 p-1"
                          title="Supprimer ce point"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Show geofence instructions for non-trajectory modes */}
            {drawingType !== 'trajectory' && !hasCreatedGeofence && (
              <div className="text-sm text-gray-600 italic p-3 bg-gray-50 rounded-lg">
                {selectedGeofenceType === 'circle' && 'Cliquez sur la carte pour créer un cercle...'}
                {selectedGeofenceType === 'polygon' && (
                  draftPolygonCount >= 3
                    ? `${draftPolygonCount} points. Cliquez encore pour en ajouter, ou Enregistrer pour garder la zone.`
                    : 'Cliquez au moins 3 points sur la carte, puis Enregistrer.'
                )}
                {selectedGeofenceType === 'point' && 'Cliquez sur la carte pour créer un point...'}
                {selectedGeofenceType === 'square' && 'Cliquez sur la carte pour créer un carré...'}
                {selectedGeofenceType === 'triangle' && 'Cliquez 3 coins pour créer un triangle...'}
              </div>
            )}
          </div>

          {/* Route Statistics */}
          {drawingType === 'trajectory' && (routeInfo.distance > 0 || routeInfo.duration > 0) && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm font-medium text-green-800 mb-1">📊 Statistiques de Route</div>
              <div className="text-xs text-green-700 space-y-1">
                {routeInfo.distance > 0 && (
                  <div>📏 Distance: {(routeInfo.distance / 1000).toFixed(1)} km</div>
                )}
                {routeInfo.duration > 0 && (
                  <div>⏱️ Durée: {Math.round(routeInfo.duration / 60)} min</div>
                )}
                {routeInfo.countries.length > 0 && (
                  <div>🌍 Pays: {routeInfo.countries.join(', ')}</div>
                )}
              </div>
            </div>
          )}

          {/* Geofence creation success message */}
          {drawingType !== 'trajectory' && hasCreatedGeofence && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ✅ Geofence created! Enter a name and click Save to store it.
              </p>
              {createdGeofencePlace && (
                <div className="mt-2 text-xs text-blue-900">
                  <div className="font-medium">
                    📍 {createdGeofencePlace.name || createdGeofencePlace.coords}
                  </div>
                  {createdGeofencePlace.name && (
                    <div className="flex items-center justify-between gap-2 text-blue-700">
                      <span>{createdGeofencePlace.coords}</span>
                      {!routeInfo.name.trim() && (
                        <button
                          type="button"
                          onClick={() => setRouteInfo(prev => ({ ...prev, name: createdGeofencePlace.name || '' }))}
                          className="underline hover:text-blue-900"
                        >
                          Utiliser comme nom
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Drawing instructions */}
          {drawingType !== 'trajectory' && !hasCreatedGeofence && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                {selectedGeofenceType === 'circle' && '⭕ Click on the map to create a circle'}
                {selectedGeofenceType === 'polygon' && '📐 Place at least 3 points, then Save'}
                {selectedGeofenceType === 'point' && '📍 Click on the map to create a point'}
                {selectedGeofenceType === 'square' && '⬜ Click on the map to create a square'}
                {selectedGeofenceType === 'triangle' && '🔺 Click on the map to create a triangle'}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2 mt-4">
            {/* Save Button */}
            {((drawingType === 'trajectory' && (isEditingMode ? editingPoints.length >= 2 : drawingPoints.length >= 2)) ||
              (drawingType !== 'trajectory' && (hasCreatedGeofence || (selectedGeofenceType === 'polygon' && draftPolygonCount >= 3)))) && routeInfo.name.trim() && (
              <button
                onClick={() => {
                  if (isEditingMode) {
                    saveEditingChanges()
                  } else if (drawingType === 'trajectory') {
                    completeDrawing()
                  } else {
                    // Handle geofence completion with proper type checking
                    if (selectedGeofenceType === 'polygon' && !(currentGeofenceRef.current instanceof google.maps.Polygon)) {
                      finalizePolygonRef.current?.()
                    }
                    const geofenceData: any = {}
                    const overlay = currentGeofenceRef.current

                    if (overlay instanceof google.maps.Circle) {
                      const center = overlay.getCenter()
                      if (center) {
                        geofenceData.center = { lat: center.lat(), lng: center.lng() }
                        geofenceData.radius = overlay.getRadius()
                      }
                    } else if (overlay instanceof google.maps.Rectangle) {
                      const rectBounds = overlay.getBounds()
                      if (rectBounds) {
                        const ne = rectBounds.getNorthEast()
                        const sw = rectBounds.getSouthWest()
                        geofenceData.bounds = [
                          { lat: sw.lat(), lng: sw.lng() },
                          { lat: sw.lat(), lng: ne.lng() },
                          { lat: ne.lat(), lng: ne.lng() },
                          { lat: ne.lat(), lng: sw.lng() }
                        ]
                      }
                    } else if (overlay instanceof google.maps.Polygon) {
                      const bounds: Array<{ lat: number, lng: number }> = []
                      overlay.getPath().forEach(latLng => bounds.push({ lat: latLng.lat(), lng: latLng.lng() }))
                      geofenceData.bounds = bounds
                    } else if (overlay instanceof google.maps.Marker) {
                      const position = overlay.getPosition()
                      if (position) geofenceData.center = { lat: position.lat(), lng: position.lng() }
                    }
                    
                    completeGeofenceDrawing(selectedGeofenceType, geofenceData)
                  }
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <span>💾</span>
                <span>{isEditingMode ? 'Update Route' : drawingType === 'trajectory' ? 'Save Route' : 'Save Geofence'}</span>
              </button>
            )}
            
            {/* Cancel Button */}
            <button
              onClick={() => {
                console.log('❌ Cancelling operation')
                setShowWaypointPanel(false)
                if (isEditingMode) {
                  clearEditing()
                } else {
                  clearDrawing()
                  clearGeofenceEditing()
                }
                
                // Notify parent to reset mode
                if (onDrawingComplete) {
                  onDrawingComplete([]) // Empty array signals cancellation
                }
              }}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>❌</span>
              <span>Cancel</span>
            </button>
          </div>

          {/* Editing Instructions */}
          {isEditingMode && (
            <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
              💡 Drag points to move them, click to delete, click on map to add
            </div>
          )}
        </div>
      )}

      {/* Map container */}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  )
}

export default CorridorMap
